import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import {
  Wrench, Users, Package, FileText, BarChart3, Globe, CheckCircle,
  ArrowRight, ArrowLeft, Star, Phone, Mail, MapPin, Shield, Zap, Clock,
  Building2, ChevronRight, Eye, EyeOff, ClipboardList, Wallet,
  BookOpen, TrendingUp, MessageSquare, UserCog, Award, Inbox,
  Upload, Printer, Sparkles, ExternalLink, Copy, Check, Info, Tag, Loader2,
  Facebook, Instagram, Youtube, Smartphone, Apple, PlayCircle, Monitor,
} from 'lucide-react';
import { Spinner } from '../components/ui/spinner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Icon resolver ──────────────────────────────────
const ICON_MAP = {
  Wrench, Users, Package, FileText, BarChart3, Globe, Shield, Zap, Clock,
  ClipboardList, Wallet, BookOpen, TrendingUp, MessageSquare, UserCog,
  Award, Inbox, Upload, Printer, Sparkles, Building2, Star, MapPin,
};
const resolveIcon = (name) => ICON_MAP[name] || Wrench;

// ── Magnetic CTA wrapper ───────────────────────────
// Ultra-subtle pull toward cursor (matches Tenant landing); coarse-pointer skip.
const MagneticButton = ({ children, strength = 0.06, max = 3, className = '' }) => {
  const ref = useRef(null);
  const handleMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let dx = (e.clientX - cx) * strength;
    let dy = (e.clientY - cy) * strength;
    if (dx > max) dx = max; else if (dx < -max) dx = -max;
    if (dy > max) dy = max; else if (dy < -max) dy = -max;
    el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
  }, [strength, max]);
  const handleLeave = useCallback(() => {
    if (ref.current) ref.current.style.transform = 'translate3d(0,0,0)';
  }, []);
  return (
    <span
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`inline-block will-change-transform transition-transform duration-700 ${className}`}
      style={{ transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' }}
    >
      {children}
    </span>
  );
};

// ── Bento CSS (injected once) ──────────────────────
const BENTO_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Epilogue:wght@700;800;900&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  .mp-bento-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: 136px;
    gap: 16px;
    overflow: visible;
  }
  @media (max-width: 1023px) {
    .mp-bento-grid {
      grid-template-columns: repeat(2, 1fr);
      grid-auto-rows: auto;
    }
    .mp-bento-grid .bc-1  { grid-column: 1 / span 2 !important; grid-row: auto !important; }
    .mp-bento-grid .bc-7  { grid-column: 1 / span 2 !important; grid-row: auto !important; }
    .mp-bento-grid .bc-9  { grid-column: auto !important; grid-row: auto !important; }
    .mp-bento-grid .bc-10 { grid-column: 1 / span 2 !important; grid-row: auto !important; }
    .mp-bento-grid [class*="bc-"] { grid-column: auto; grid-row: auto; }
  }
  @media (max-width: 639px) {
    .mp-bento-grid [class*="bc-"] { grid-column: auto !important; grid-row: auto !important; }
  }

  /* Card 2: less inner padding in expanded view only */
  .bc-2.expanded .mp-bento-inner {
    padding: 0.8rem;
  }

  /* Card 8: slightly less vertical padding in expanded view */
  .bc-8.expanded .mp-bento-inner {
    padding-top: 0.8rem;
    padding-bottom: 0.8rem;
  }

  /* Card 1 (L): higher-than-center alignment with more spacing */
  .bc-1 .mp-bento-inner {
    padding-top: 8%;
  }
  .bc-1.expanded .mp-bento-inner {
    padding-top: 1rem;
  }
  .bc-1 .mp-tier-pill {
    margin-bottom: 10px;
  }
  .bc-1 .mp-card-icon-box {
    width: 42px;
    height: 42px;
    margin-bottom: 4px;
  }
  .bc-1 .mp-card-icon-box svg {
    width: 22px;
    height: 22px;
  }
  .bc-1 .mp-card-title {
    margin-top: 8px;
  }
  .bc-1 .mp-card-tagline {
    margin-top: 6px;
  }

  /* Cards 7, 10 (W): higher-than-center alignment */
  .bc-7 .mp-bento-inner,
  .bc-10 .mp-bento-inner {
    padding-top: 3%;
  }
  .bc-7.expanded .mp-bento-inner,
  .bc-10.expanded .mp-bento-inner {
    padding-top: 1rem;
  }
  /* Wide cards: indent expanded subtitle to clear the icon */
  .bc-7 .mp-exp-content > p:first-child,
  .bc-10 .mp-exp-content > p:first-child {
    margin-left: 46px;
  }

  /* Card 9 (T): higher-than-center alignment */
  .bc-9 .mp-bento-inner {
    padding-top: 15%;
  }
  .bc-9.expanded .mp-bento-inner {
    padding-top: 1rem;
  }

  .bc-1  { grid-column: 1 / span 2; grid-row: 1 / span 2; }
  .bc-2  { grid-column: 3;          grid-row: 1; }
  .bc-3  { grid-column: 4;          grid-row: 1; }
  .bc-4  { grid-column: 3;          grid-row: 2; }
  .bc-5  { grid-column: 4;          grid-row: 2; }
  .bc-6  { grid-column: 1;          grid-row: 3; }
  .bc-7  { grid-column: 2 / span 2; grid-row: 3; }
  .bc-8  { grid-column: 4;          grid-row: 3; }
  .bc-9  { grid-column: 1;          grid-row: 4 / span 2; }
  .bc-10 { grid-column: 2 / span 2; grid-row: 4; }
  .bc-11 { grid-column: 4;          grid-row: 4; }
  .bc-12 { grid-column: 2;          grid-row: 5; }
  .bc-13 { grid-column: 3;          grid-row: 5; }
  .bc-14 { grid-column: 4;          grid-row: 5; }

  .mp-bento-card {
    position: relative;
    overflow: visible;
    cursor: pointer;
    z-index: 1;
    transition: z-index 0s 0.56s, opacity 0.38s ease, filter 0.38s ease, transform 0.38s ease;
  }
  .mp-bento-card.expanded {
    z-index: 30;
    transition: z-index 0s;
  }
  .mp-bento-grid.has-expanded .mp-bento-card:not(.expanded) {
    opacity: 0.4;
    filter: grayscale(0.3) blur(0.6px);
    transform: scale(0.975);
  }

  .mp-bento-inner {
    position: absolute;
    inset: 0;
    height: var(--cell-h);
    background: #ffffff;
    border-radius: 20px;
    border: 1.5px solid #e8eeeb;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: var(--inner-p, 1rem);
    transform-origin: var(--origin, top left);
    transform: scale(1);
    box-shadow: 0 1px 4px rgba(0,0,0,.05), 0 0 0 0 rgba(16,185,129,0);
    transition:
      transform   0.52s cubic-bezier(0.34, 1.28, 0.64, 1),
      height      0.48s cubic-bezier(0.4, 0, 0.2, 1),
      padding-top 0.45s cubic-bezier(0.4, 0, 0.2, 1),
      box-shadow  0.4s ease,
      border-color 0.3s ease;
  }
  @media (max-width: 1023px) {
    .mp-bento-inner {
      position: relative;
      height: auto !important;
    }
  }
  .mp-bento-card.expanded .mp-bento-inner {
    transform: scale(var(--scale, 1.5));
    height: var(--exp-h, 300px);
    overflow: hidden;
    box-shadow:
      0 32px 80px -8px rgba(4,120,87,0.22),
      0 8px 24px -4px rgba(0,0,0,0.12),
      0 0 0 1.5px rgba(4,120,87,0.3);
    border-color: rgba(4,120,87,0.25);
  }
  @media (max-width: 1023px) {
    .mp-bento-card.expanded .mp-bento-inner {
      transform: none !important;
      height: auto !important;
      box-shadow: 0 8px 32px -4px rgba(4,120,87,0.18);
    }
  }

  .mp-card-num {
    position: absolute;
    top: 12px;
    right: 14px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: rgba(4,120,87,0.22);
    transition: color 0.3s ease;
    pointer-events: none;
  }
  .mp-bento-card.expanded .mp-card-num { color: rgba(4,120,87,0.5); }

  /* ── Tier pill badges ── */
  .mp-tier-pill {
    display: inline-flex;
    align-items: center;
    padding: 1.5px 7px;
    border-radius: 100px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 7.5px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    width: fit-content;
    margin-bottom: 6px;
    line-height: 1.3;
  }
  .mp-tier-standard {
    background: rgba(4,120,87,0.1);
    color: #047857;
    border: 1px solid rgba(4,120,87,0.25);
  }
  .mp-tier-premium {
    background: linear-gradient(135deg, rgba(245,158,11,0.12), rgba(217,119,6,0.15));
    color: #92400e;
    border: 1px solid rgba(245,158,11,0.3);
  }

  /* ── Card icon box ── */
  .mp-card-icon-box {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: rgba(4,120,87,0.08);
    color: #047857;
  }
  .mp-card-icon-box.mp-icon-premium {
    background: rgba(4,120,87,0.06);
    color: #047857;
  }

  /* ── Card title: slides from below-icon to beside-icon on expand ── */
  .mp-card-title {
    margin-top: 6px;
    transition: transform 0.42s cubic-bezier(0.34,1.12,0.64,1), margin-bottom 0.35s ease;
    transform: translate(0, 0);
    will-change: transform;
    position: relative;
    z-index: 2;
  }
  .mp-bento-card.expanded .mp-card-title {
    transform: translate(calc(36px + 10px), -32px);
    margin-bottom: -20px;
  }
  /* Card 1 only: extra title push (42px icon) */
  .bc-1.expanded .mp-card-title {
    transform: translate(calc(42px + 10px), -44px);
    margin-bottom: -30px;
  }

  /* ── Short tagline (collapses on expand) ── */
  .mp-card-tagline {
    margin-top: 2px;
    overflow: hidden;
    line-height: 1.4;
    transition: opacity 0.22s ease, max-height 0.3s ease, margin-top 0.3s ease;
    opacity: 1;
    max-height: 40px;
  }
  .mp-bento-card.expanded .mp-card-tagline {
    opacity: 0;
    max-height: 0;
    margin-top: 0;
  }

  .mp-exp-content {
    overflow: hidden;
    max-height: 0;
    opacity: 0;
    transform: translateY(6px);
    transition: max-height 0.42s ease, opacity 0.28s ease 0.12s, transform 0.38s ease 0.1s;
  }
  .mp-bento-card.expanded .mp-exp-content {
    max-height: 2000px;
    opacity: 1;
    transform: translateY(0);
    margin-top: 2px;
  }
  @media (max-width: 1023px) {
    .mp-exp-content {
      max-height: none !important;
      opacity: 1 !important;
      transform: none !important;
      margin-top: 0.5rem;
    }
  }

  .mp-plan-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    padding: 2px 8px 3px;
    border-radius: 100px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    width: fit-content;
  }
  .mp-badge-std  { background: rgba(4,120,87,0.08); color: #047857; border: 1px solid rgba(4,120,87,0.2); }
  .mp-badge-prem { background: rgba(245,158,11,0.08); color: #b45309; border: 1px solid rgba(245,158,11,0.25); }

  /* Dhaka map */
  .mp-map-pin {
    position: absolute;
    transform: translate(-50%, -50%);
  }
  .mp-map-pin .pin-dot {
    width: 10px; height: 10px;
    background: #10b981;
    border-radius: 50%;
    position: relative;
    z-index: 2;
    box-shadow: 0 0 6px rgba(16,185,129,0.6);
  }
  .mp-map-pin .pin-ring {
    position: absolute;
    top: 50%; left: 50%;
    width: 24px; height: 24px;
    margin: -12px 0 0 -12px;
    border-radius: 50%;
    border: 2px solid rgba(16,185,129,0.4);
    animation: mp-ping 2s cubic-bezier(0,0,0.2,1) infinite;
  }
  .mp-map-pin:nth-child(2n) .pin-ring { animation-delay: 0.5s; }
  .mp-map-pin:nth-child(3n) .pin-ring { animation-delay: 1s; }
  .mp-map-pin:nth-child(5n) .pin-ring { animation-delay: 1.5s; }
  @keyframes mp-ping {
    0% { transform: scale(1); opacity: 0.7; }
    75%, 100% { transform: scale(2.5); opacity: 0; }
  }

  /* Hero floating elements */
  @keyframes mp-float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(2deg); }
  }
  @keyframes mp-float-delay {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-15px) rotate(-2deg); }
  }
  @keyframes mp-gradient-shift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  .mp-hero-bg {
    background: linear-gradient(135deg, #022c22, #064e3b, #065f46, #047857, #059669, #10b981, #047857, #064e3b, #022c22);
    background-size: 600% 600%;
    animation: mp-gradient-shift 14s ease infinite;
    position: relative;
    isolation: isolate;
  }
  /* Ambient breathing glow — always alive, fills the pause between strikes */
  .mp-hero-bg > .mp-cta-ambient {
    position: absolute;
    inset: -10%;
    pointer-events: none;
    background:
      radial-gradient(60% 50% at 22% 28%, rgba(255,255,255,0.16), transparent 70%),
      radial-gradient(55% 45% at 78% 72%, rgba(255,255,255,0.13), transparent 70%);
    mix-blend-mode: screen;
    animation: mp-cta-breathe 7s ease-in-out infinite;
    z-index: 0;
  }
  /* Two pools breathe out of phase so the section never goes flat */
  .mp-hero-bg > .mp-cta-ambient::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(50% 42% at 70% 30%, rgba(255,255,255,0.10), transparent 70%),
      radial-gradient(50% 42% at 28% 75%, rgba(255,255,255,0.09), transparent 70%);
    mix-blend-mode: screen;
    animation: mp-cta-breathe-alt 9s ease-in-out infinite;
  }
  @keyframes mp-cta-breathe {
    0%, 100% { opacity: 0.35; transform: translate3d(0,0,0) scale(0.97); }
    50%      { opacity: 1;    transform: translate3d(0,0,0) scale(1.06); }
  }
  @keyframes mp-cta-breathe-alt {
    0%, 100% { opacity: 1;    transform: translate3d(0,0,0) scale(1.05); }
    50%      { opacity: 0.3;  transform: translate3d(0,0,0) scale(0.96); }
  }
  /* Layered crossing diagonal sheens — TL↔BR and TR↔BL only.
     Two main beams + two faint trailing beams for depth. All mix-blend
     screen so they read as light. Different durations & delays prevent
     any synchronized 'metronome' feel. */
  .mp-hero-bg::before,
  .mp-hero-bg::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-repeat: no-repeat;
    mix-blend-mode: screen;
  }
  .mp-hero-bg::before {
    /* TL→BR primary beam + faint follower (no rest, runs full cycle) */
    background-image:
      linear-gradient(135deg, transparent 44%, rgba(255,255,255,0.085) 50%, transparent 56%),
      linear-gradient(135deg, transparent 46%, rgba(255,255,255,0.05) 50%, transparent 54%);
    background-size: 220% 220%, 320% 320%;
    background-position: -120% -120%, -160% -160%;
    animation:
      mp-shine-tlbr 7s ease-in-out infinite,
      mp-shine-tlbr-soft 13s linear infinite;
  }
  .mp-hero-bg::after {
    /* TR→BL primary beam + faint follower (no rest) */
    background-image:
      linear-gradient(45deg, transparent 44%, rgba(255,255,255,0.07) 50%, transparent 56%),
      linear-gradient(45deg, transparent 46%, rgba(255,255,255,0.045) 50%, transparent 54%);
    background-size: 220% 220%, 320% 320%;
    background-position: 220% -120%, 260% -160%;
    animation:
      mp-shine-trbl 9s ease-in-out infinite 1.2s,
      mp-shine-trbl-soft 16s linear infinite 3s;
  }
  @keyframes mp-shine-tlbr {
    0%        { background-position: -120% -120%, -160% -160%; }
    55%, 100% { background-position: 220% 220%,    260% 260%; }
  }
  /* Soft trail keeps drifting through the pause — no rest stop */
  @keyframes mp-shine-tlbr-soft {
    0%   { background-position: -120% -120%, -160% -160%; }
    100% { background-position: 220% 220%,    260% 260%; }
  }
  @keyframes mp-shine-trbl {
    0%        { background-position: 220% -120%, 260% -160%; }
    55%, 100% { background-position: -120% 220%, -160% 260%; }
  }
  @keyframes mp-shine-trbl-soft {
    0%   { background-position: 220% -120%, 260% -160%; }
    100% { background-position: -120% 220%, -160% 260%; }
  }

  /* Pricing cards */
  .mp-pricing-card {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .mp-pricing-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 32px 64px -12px rgba(0,0,0,0.15);
  }

  /* Shared animations */
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Bar fills animate on expand */
  .mp-bar-fill {
    height: 100%;
    border-radius: 100px;
    background: #10b981;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.65s cubic-bezier(0.4,0,0.2,1) 0.3s;
  }
  .mp-bento-card.expanded .mp-bar-fill { transform: scaleX(1); }

  /* Job pipeline */
  .mp-job-pipe { display: flex; align-items: center; gap: 0; margin-top: 10px; }
  .mp-job-stage {
    flex: 1;
    text-align: center;
    padding: 4px 2px;
    border-radius: 6px;
    font-size: 7.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .mp-job-arrow { color: rgba(16,185,129,0.25); flex-shrink: 0; font-size: 14px; }
  .mp-stage-pending  { background: rgba(251,191,36,0.12); color: #92620a; }
  .mp-stage-progress { background: rgba(56,189,248,0.12); color: #005680; }
  .mp-stage-ready    { background: rgba(167,139,250,0.12); color: #5b3fad; }
  .mp-stage-deliver  { background: rgba(4,120,87,0.12); color: #047857; }

  /* Mini sparkline bars */
  .mp-spark-bar {
    flex: 1;
    border-radius: 2px 2px 0 0;
    transform: scaleY(0);
    transform-origin: bottom;
    transition: transform 0.5s cubic-bezier(0.4,0,0.2,1) var(--delay, 0.3s);
  }
  .mp-bento-card.expanded .mp-spark-bar { transform: scaleY(1); }

  /* Section reveal */
  .mp-reveal {
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .mp-reveal.visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* ── Garage cards horizontal marquee ───────────────── */
  .mp-garage-marquee {
    overflow: hidden;
    position: relative;
    mask-image: linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%);
  }
  .mp-garage-track {
    display: flex;
    gap: 20px;
    width: max-content;
    animation: mp-garage-scroll 38s linear infinite;
  }
  .mp-garage-marquee:hover .mp-garage-track { animation-play-state: paused; }
  .mp-garage-card-wrap {
    flex: 0 0 auto;
    width: 320px;
    max-width: 80vw;
  }
  @keyframes mp-garage-scroll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }
  @media (max-width: 639px) {
    .mp-garage-card-wrap { width: 240px; }
    .mp-garage-track { gap: 12px; animation-duration: 30s; }
  }

  /* Leaflet map overrides */
  .leaflet-marker-icon {
    background: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  .mp-lf-pin {
    position: relative;
    width: 22px;
    height: 22px;
  }
  .mp-lf-pin .lf-core {
    width: 10px;
    height: 10px;
    background: radial-gradient(circle, #34d399 0%, #10b981 60%, #059669 100%);
    border-radius: 50%;
    position: absolute;
    top: 6px;
    left: 6px;
    z-index: 2;
    box-shadow: 0 0 8px rgba(16,185,129,0.6), 0 0 16px rgba(16,185,129,0.3);
  }
  .mp-lf-pin .lf-pulse {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid rgba(16,185,129,0.45);
    animation: mp-ping 2s cubic-bezier(0,0,0.2,1) infinite;
  }
  .mp-lf-pin .lf-pulse2 {
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 1.5px solid rgba(16,185,129,0.2);
    animation: mp-ping 2s cubic-bezier(0,0,0.2,1) infinite;
    animation-delay: 0.5s;
  }
  /* Map badge slide transition */
  .mp-badge-text {
    transition: opacity 0.35s ease, transform 0.35s ease;
  }
  .mp-badge-text.mp-badge-exit {
    opacity: 0;
    transform: translateY(-6px);
  }
  .mp-badge-text.mp-badge-enter {
    opacity: 1;
    transform: translateY(0);
  }
  /* Map dot indicators */
  .mp-map-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: rgba(100, 116, 139, 0.25);
    transition: all 0.4s ease;
    cursor: pointer;
  }
  .mp-map-dot.active {
    background: #009137;
    width: 24px; border-radius: 4px;
    box-shadow: 0 0 8px rgba(0,145,55,0.45);
  }
  /* Map scroll hint pulse */
  @keyframes mp-scroll-hint {
    0%, 100% { transform: translateY(0); opacity: 0.7; }
    50% { transform: translateY(4px); opacity: 1; }
  }
  .mp-scroll-hint {
    animation: mp-scroll-hint 1.5s ease-in-out infinite;
  }

  /* ══════════ MOBILE FEATURE CARDS ══════════ */
  @media (max-width: 639px) {
    /* 2-column compact grid */
    .mp-bento-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      grid-auto-rows: auto !important;
      gap: 10px !important;
    }
    /* Reset ALL card grid positions for mobile */
    .mp-bento-grid [class*="bc-"] {
      grid-column: auto !important;
      grid-row: auto !important;
    }
    /* Cards 1, 7, 10 span full width */
    .mp-bento-grid .bc-1,
    .mp-bento-grid .bc-7,
    .mp-bento-grid .bc-10 {
      grid-column: 1 / -1 !important;
    }

    /* Card entrance animation */
    .mp-bento-card {
      opacity: 0;
      transform: translateY(32px) scale(0.97);
      transition: opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1),
                  transform 0.65s cubic-bezier(0.22, 1, 0.36, 1) !important;
      filter: none !important;
    }
    .mp-bento-card.mp-mob-visible {
      opacity: 1 !important;
      transform: translateY(0) scale(1) !important;
      filter: none !important;
    }

    /* Prevent expansion dimming */
    .mp-bento-grid.has-expanded .mp-bento-card:not(.expanded) {
      opacity: 0 !important;
      filter: none !important;
      transform: translateY(32px) scale(0.97) !important;
    }
    .mp-bento-grid.has-expanded .mp-bento-card.mp-mob-visible:not(.expanded) {
      opacity: 1 !important;
      filter: none !important;
      transform: translateY(0) scale(1) !important;
    }

    /* Card inner - compact mobile design */
    .mp-bento-inner {
      position: relative !important;
      height: auto !important;
      transform: none !important;
      padding: 14px !important;
      border-radius: 18px !important;
      border: 1px solid rgba(16,185,129,0.12) !important;
      border-top: 3px solid #10b981 !important;
      border-left: 1px solid rgba(16,185,129,0.12) !important;
      box-shadow:
        0 2px 12px -4px rgba(0,0,0,0.06),
        0 1px 3px -1px rgba(0,0,0,0.03) !important;
      background: #ffffff !important;
    }
    .mp-bento-card.expanded .mp-bento-inner {
      transform: none !important;
      height: auto !important;
      box-shadow:
        0 2px 12px -4px rgba(0,0,0,0.06),
        0 1px 3px -1px rgba(0,0,0,0.03) !important;
      border-color: rgba(16,185,129,0.12) !important;
      border-top-color: #10b981 !important;
    }

    /* Premium card accent */
    .mp-bento-inner.mp-mob-premium {
      border-top-color: #f59e0b !important;
    }

    /* Full-width cards get slightly more padding */
    .bc-1 .mp-bento-inner {
      padding: 18px !important;
    }
    .bc-7 .mp-bento-inner,
    .bc-10 .mp-bento-inner {
      padding: 16px !important;
    }

    /* Override ALL card-specific padding */
    .bc-2.expanded .mp-bento-inner,
    .bc-8.expanded .mp-bento-inner,
    .bc-1.expanded .mp-bento-inner, .bc-7.expanded .mp-bento-inner,
    .bc-9 .mp-bento-inner, .bc-9.expanded .mp-bento-inner,
    .bc-10.expanded .mp-bento-inner {
      padding: 14px !important;
    }
    .bc-1.expanded .mp-bento-inner { padding: 18px !important; }
    .bc-7.expanded .mp-bento-inner,
    .bc-10.expanded .mp-bento-inner { padding: 16px !important; }

    /* Title - no slide, compact */
    .mp-card-title,
    .mp-bento-card.expanded .mp-card-title,
    .bc-1.expanded .mp-card-title,
    .bc-1 .mp-card-title {
      transform: none !important;
      margin-bottom: 0 !important;
    }
    /* Small cards: smaller title */
    .mp-card-title {
      font-size: 13px !important;
      line-height: 1.35 !important;
    }
    /* Full-width cards: bigger title */
    .bc-1 .mp-card-title,
    .bc-1.expanded .mp-card-title {
      font-size: 17px !important;
    }
    .bc-7 .mp-bento-inner h3,
    .bc-10 .mp-bento-inner h3 {
      font-size: 15px !important;
    }

    /* Tagline - always visible, compact */
    .mp-card-tagline,
    .mp-bento-card.expanded .mp-card-tagline {
      opacity: 1 !important;
      max-height: none !important;
      margin-top: 4px !important;
      font-size: 11px !important;
      line-height: 1.45 !important;
      color: #64748b !important;
    }
    /* Full-width tagline bigger */
    .bc-1 .mp-card-tagline { font-size: 13px !important; }
    .bc-7 .mp-card-tagline,
    .bc-10 .mp-card-tagline { font-size: 12px !important; }

    /* Expanded content - always visible with subtle separator */
    .mp-exp-content,
    .mp-bento-card.expanded .mp-exp-content {
      max-height: none !important;
      opacity: 1 !important;
      transform: none !important;
      margin-top: 10px !important;
      padding-top: 10px !important;
      border-top: 1px dashed rgba(16,185,129,0.15);
    }
    .mp-bento-inner.mp-mob-premium .mp-exp-content {
      border-top-color: rgba(245,158,11,0.2);
    }

    .mp-exp-content > p:first-child {
      font-size: 11px !important;
      line-height: 1.5 !important;
      margin-left: 0 !important;
    }
    /* Full-width exp text bigger */
    .bc-1 .mp-exp-content > p:first-child { font-size: 13px !important; }
    .bc-7 .mp-exp-content > p:first-child,
    .bc-10 .mp-exp-content > p:first-child { font-size: 12px !important; }

    /* Tier pill */
    .mp-tier-pill {
      font-size: 7px !important;
      padding: 1.5px 8px !important;
      margin-bottom: 8px !important;
    }
    .bc-1 .mp-tier-pill {
      font-size: 8px !important;
      margin-bottom: 10px !important;
    }

    /* Icon box - smaller for 2-col */
    .mp-card-icon-box {
      width: 32px !important;
      height: 32px !important;
      border-radius: 10px !important;
      margin-bottom: 6px !important;
    }
    .mp-card-icon-box svg {
      width: 16px !important;
      height: 16px !important;
    }
    /* Full-width cards: slightly bigger icon */
    .bc-1 .mp-card-icon-box {
      width: 40px !important;
      height: 40px !important;
      border-radius: 12px !important;
      margin-bottom: 8px !important;
    }
    .bc-1 .mp-card-icon-box svg {
      width: 20px !important;
      height: 20px !important;
    }

    /* Card number */
    .mp-card-num {
      font-size: 9px !important;
      top: 12px !important;
      right: 14px !important;
    }

    /* Plan badges - compact for 2-col */
    .mp-plan-badge {
      font-size: 8px !important;
      padding: 2px 7px !important;
    }
    /* Full-width badges bigger */
    .bc-1 .mp-plan-badge,
    .bc-7 .mp-plan-badge,
    .bc-10 .mp-plan-badge {
      font-size: 10px !important;
      padding: 3px 10px !important;
    }

    /* Job pipeline */
    .mp-job-pipe { margin-top: 10px !important; }
    .mp-job-stage {
      font-size: 8px !important;
      padding: 5px 2px !important;
      border-radius: 6px !important;
    }
    .bc-1 .mp-job-stage {
      font-size: 9px !important;
      padding: 6px 3px !important;
    }

    /* Bar fills - always visible */
    .mp-bar-fill { transform: scaleX(1) !important; }
    .mp-spark-bar { transform: scaleY(1) !important; }

    /* Wide card subtitle indent override */
    .bc-7 .mp-exp-content > p:first-child,
    .bc-10 .mp-exp-content > p:first-child {
      margin-left: 0 !important;
    }

    /* Wide card layout wrapper override */
    .mp-wide-layout {
      margin-top: 0 !important;
      margin-left: 0 !important;
    }

    /* Grid content inside expanded */
    .mp-exp-content .grid { gap: 4px !important; }
    .mp-exp-content .grid > div {
      padding: 6px 4px !important;
      border-radius: 8px !important;
    }
    .bc-1 .mp-exp-content .grid { gap: 6px !important; }
    .bc-1 .mp-exp-content .grid > div {
      padding: 8px 6px !important;
      border-radius: 10px !important;
    }

    /* Card 1 expanded grid text */
    .mp-exp-content .grid.grid-cols-3 > div .text-sm {
      font-size: 11px !important;
    }
    .mp-exp-content .grid.grid-cols-3 > div .text-\[9px\] {
      font-size: 8px !important;
    }
    .bc-1 .mp-exp-content .grid.grid-cols-3 > div .text-sm {
      font-size: 13px !important;
    }
    .bc-1 .mp-exp-content .grid.grid-cols-3 > div .text-\[9px\] {
      font-size: 9px !important;
    }

    /* Income/expense card */
    .bc-9 .mp-exp-content .text-lg {
      font-size: 20px !important;
    }

    /* Stats in small cards */
    .mp-exp-content .text-\[12px\] { font-size: 11px !important; }
    .mp-exp-content .text-\[11px\] { font-size: 10px !important; }
    .mp-exp-content .text-\[9px\] { font-size: 8px !important; }
    .mp-exp-content .text-\[8\.5px\] { font-size: 8px !important; }
    .mp-exp-content .text-\[8px\] { font-size: 7.5px !important; }
    .mp-exp-content .text-\[7\.5px\] { font-size: 7px !important; }
    .mp-exp-content .text-\[7px\] { font-size: 6.5px !important; }

    /* Full-width card stats override - bigger */
    .bc-1 .mp-exp-content .text-\[12px\] { font-size: 12px !important; }
    .bc-7 .mp-exp-content .text-\[12px\] { font-size: 12px !important; }
    .bc-10 .mp-exp-content .text-\[12px\] { font-size: 12px !important; }

    /* Map dot on mobile - larger for touch */
    .mp-map-dot {
      width: 12px !important;
      height: 12px !important;
    }
    .mp-map-dot.active {
      width: 32px !important;
    }

    /* Hide scroll hint on mobile */
    .mp-scroll-hint {
      display: none !important;
    }

    /* Features section - less padding on mobile */
    #features { padding-top: 3rem !important; padding-bottom: 3rem !important; }
    #features h2 { font-size: 1.5rem !important; }
    #features > div > div:first-child p { font-size: 14px !important; }

    /* Equal height for side-by-side cards in same row */
    .mp-bento-inner {
      height: 100% !important;
      display: flex !important;
      flex-direction: column !important;
    }
    .mp-exp-content {
      flex: 1 !important;
    }

    /* Pill badges - smaller for 2-col mobile */
    .mp-plan-badge {
      font-size: 6.5px !important;
      padding: 1.5px 5px 2px !important;
      letter-spacing: 0.04em !important;
    }
    .mp-exp-content .flex.gap-1 {
      gap: 3px !important;
    }
    .mp-exp-content .flex.justify-evenly {
      justify-content: flex-start !important;
      flex-wrap: wrap !important;
    }

    /* Staggered content entrance animations */
    .mp-bento-card .mp-tier-pill,
    .mp-bento-card .mp-card-icon-box,
    .mp-bento-card .mp-card-title,
    .mp-bento-card .mp-card-tagline,
    .mp-bento-card .mp-exp-content {
      opacity: 0;
      transform: translateY(14px);
      transition: opacity 0.45s ease, transform 0.45s ease;
    }
    .mp-bento-card.mp-mob-visible .mp-tier-pill {
      opacity: 1; transform: translateY(0);
      transition-delay: 0.08s;
    }
    .mp-bento-card.mp-mob-visible .mp-card-icon-box {
      opacity: 1; transform: translateY(0);
      transition-delay: 0.14s;
    }
    .mp-bento-card.mp-mob-visible .mp-card-title {
      opacity: 1; transform: translateY(0);
      transition-delay: 0.20s;
    }
    .mp-bento-card.mp-mob-visible .mp-card-tagline {
      opacity: 1; transform: translateY(0);
      transition-delay: 0.28s;
    }
    .mp-bento-card.mp-mob-visible .mp-exp-content {
      opacity: 1 !important; transform: translateY(0);
      transition-delay: 0.36s;
    }

    /* Feature progress bar */
    .mp-feature-progress {
      position: sticky;
      top: 58px;
      z-index: 40;
      background: rgba(255,255,255,0.85);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border-bottom: 1px solid rgba(16,185,129,0.1);
      padding: 8px 16px;
    }
    .mp-feature-progress-bar {
      height: 3px;
      background: #e2e8f0;
      border-radius: 4px;
      overflow: hidden;
    }
    .mp-feature-progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #10b981, #059669);
      border-radius: 4px;
      transition: width 0.3s ease;
    }
  }

  /* Map auto-play indicator (all views) — subtle timer on the side */
  .mp-map-autoplay {
    position: absolute;
    bottom: 60px;
    right: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    z-index: 1000;
    cursor: pointer;
  }
  @media (max-width: 639px) {
    .mp-map-autoplay {
      bottom: 80px;
    }
  }
  .mp-map-timer-ring {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255,255,255,0.7);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(0,145,55,0.18);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .mp-map-timer-ring svg.mp-ring-svg {
    width: 32px;
    height: 32px;
    transform: rotate(-90deg);
    position: absolute;
    top: 0;
    left: 0;
    overflow: visible;
  }
  .mp-map-timer-ring svg circle {
    fill: none;
    stroke-width: 2;
  }
  .mp-map-timer-ring svg circle.mp-timer-track {
    stroke: rgba(0,0,0,0.06);
  }
  .mp-map-timer-ring svg circle.mp-timer-fill {
    stroke: #009137;
    stroke-dasharray: 81.7;
    stroke-dashoffset: 81.7;
    stroke-linecap: round;
    animation: mp-timer-countdown 5s linear infinite;
  }
  .mp-map-autoplay.paused .mp-timer-fill {
    animation-play-state: paused;
  }
  @keyframes mp-timer-countdown {
    from { stroke-dashoffset: 81.7; }
    to { stroke-dashoffset: 0; }
  }
  @keyframes mp-autoplay-progress {
    from { width: 0%; }
    to { width: 100%; }
  }
  .mp-map-progress-bar {
    display: none;
  }
  .mp-map-progress-fill {
    display: none;
  }
`;

// ── Bento size configs ─────────────────────────────
const SIZE_CFG = {
  L: { scale: 1.12, extra: 30 },
  W: { scale: 1.12, extra: 60 },
  T: { scale: 1.12, extra: 30 },
  S: { scale: 1.12, extra: 60 },
};
// Per-card expanded overrides { extra: height added to cellH }
const CARD_EXTRA = {
  'lc-03': { extra: 64 },                 // card 3
  'lc-04': { extra: 58 },                 // card 4
  'lc-05': { extra: 68 },                 // card 5
  'lc-06': { extra: 68 },                 // card 6
  'lc-07': { extra: 80 },                 // card 7: pills
  'lc-08': { extra: 72 },                 // card 8: loyalty pills
  'lc-09': { extra: 40 },                 // card 9: expense breakdown
  'lc-11': { extra: 50 },                 // card 11: smaller pills, less height
  'lc-12': { extra: 74 },                 // card 12: loyalty content
  'lc-13': { extra: 76 },                 // card 13: parts request
  'lc-14': { extra: 62 },                 // card 14: AI import pills
};

// ── Short display titles per card (matching screenshot) ──
const CARD_SHORT_TITLES = {
  'lc-01': { en: 'End-to-End Job & Workflow Management', bn: 'সম্পূর্ণ জব ও ওয়ার্কফ্লো ম্যানেজমেন্ট' },
  'lc-02': { en: 'Free Garage Website', bn: 'ফ্রি গ্যারেজ ওয়েবসাইট' },
  'lc-03': { en: 'Invoices & Quotations', bn: 'ইনভয়েস ও কোটেশন' },
  'lc-04': { en: 'Dues Tracking & Management', bn: 'বকেয়া ট্র্যাকিং ও ম্যানেজমেন্ট' },
  'lc-05': { en: 'Parts & Inventory', bn: 'পার্টস ও ইনভেন্টরি' },
  'lc-06': { en: 'Service Catalog', bn: 'সার্ভিস ক্যাটালগ' },
  'lc-07': { en: 'Detailed Reporting with Data Visualization', bn: 'ডেটা ভিজ্যুয়ালাইজেশন সহ বিস্তারিত রিপোর্টিং' },
  'lc-08': { en: 'Intelligent Customer Profile', bn: 'বুদ্ধিমান গ্রাহক প্রোফাইল' },
  'lc-09': { en: 'Income vs. Expense Analysis', bn: 'আয় বনাম ব্যয় বিশ্লেষণ' },
  'lc-10': { en: 'Smart SMS Reminder & SMS Campaigns', bn: 'স্মার্ট SMS রিমাইন্ডার ও ক্যাম্পেইন' },
  'lc-11': { en: 'Employee, Security & Payroll', bn: 'কর্মচারী, নিরাপত্তা ও পে-রোল' },
  'lc-12': { en: 'Loyalty & Referrals', bn: 'লয়্যালটি ও রেফারেল' },
  'lc-13': { en: 'Customer > Parts Request', bn: 'গ্রাহক > পার্টস রিকোয়েস্ট' },
  'lc-14': { en: 'AI Inventory Upload', bn: 'AI ইনভেন্টরি আপলোড' },
};

// ── Short taglines (collapsed state) ──
const CARD_TAGLINES = {
  'lc-01': { en: 'Create, track and close every job — from walk-in to delivery — without a single sticky note.', bn: 'প্রতিটি জব তৈরি, ট্র্যাক এবং ক্লোজ করুন — কোনো স্টিকি নোট ছাড়াই।' },
  'lc-02': { en: 'Your garage goes online — for free.', bn: 'আপনার গ্যারেজ অনলাইনে যায় — বিনামূল্যে।' },
  'lc-03': { en: 'Professional PDFs, shared in seconds.', bn: 'পেশাদার PDF, সেকেন্ডে শেয়ার।' },
  'lc-04': { en: "Never forget what you're owed.", bn: 'আপনার পাওনা কখনো ভুলবেন না।' },
  'lc-05': { en: "Always know what's on your shelf.", bn: 'সবসময় জানুন আপনার শেলফে কি আছে।' },
  'lc-06': { en: 'Showcase every service you offer.', bn: 'আপনার প্রতিটি সার্ভিস প্রদর্শন করুন।' },
  'lc-07': { en: 'See exactly where your money comes from — and where it goes.', bn: 'দেখুন আপনার টাকা কোথা থেকে আসে — এবং কোথায় যায়।' },
  'lc-08': { en: 'Every customer, every vehicle, every visit — one place.', bn: 'প্রতিটি গ্রাহক, প্রতিটি বাইক, প্রতিটি ভিজিট — এক জায়গায়।' },
  'lc-09': { en: 'Finally know your real profit — not just revenue.', bn: 'অবশেষে জানুন আপনার আসল লাভ — শুধু রেভিনিউ নয়।' },
  'lc-10': { en: 'Bring customers back before they forget you exist.', bn: 'গ্রাহকদের ফিরিয়ে আনুন তারা আপনাকে ভুলে যাওয়ার আগে।' },
  'lc-11': { en: 'Manage your team. Pay them right.', bn: 'আপনার টিম পরিচালনা করুন। সঠিক বেতন দিন।' },
  'lc-12': { en: 'Turn your best customers into marketers.', bn: 'আপনার সেরা গ্রাহকদের মার্কেটারে পরিণত করুন।' },
  'lc-13': { en: 'Customers order parts via SMS — you receive them in-app.', bn: 'গ্রাহকরা পার্টস অর্ডার করেন — আপনি অ্যাপে পান।' },
  'lc-14': { en: 'Add 100 parts from a photo. Really.', bn: 'একটি ছবি থেকে ১০০ পার্টস যোগ করুন। সত্যিই।' },
};

// ── Expanded subtitles per card ──
const CARD_EXPANDED_SUB = {
  'lc-01': { en: 'Create digital job cards for every service or sale and track progress in real-time and manage the entire shop workflow from start to finish.', bn: 'প্রতিটি সার্ভিস বা বিক্রয়ের জন্য ডিজিটাল জব কার্ড তৈরি করুন এবং রিয়েল-টাইমে অগ্রগতি ট্র্যাক করুন।' },
  'lc-02': { en: 'A custom website that shows your location, services, and parts pricing to local riders. Customers can book slots and request quotes directly, turning online visitors into shop appointments.', bn: 'আপনার অবস্থান, সার্ভিস এবং পার্টস মূল্য দেখানো একটি কাস্টম ওয়েবসাইট।' },
  'lc-03': { en: "Track every job from start to finish with a printable, seamless workflow that sends professional digital invoices directly to your customers' phones.", bn: 'পেশাদার ডিজিটাল ইনভয়েস সরাসরি আপনার গ্রাহকদের ফোনে পাঠান।' },
  'lc-04': { en: 'Easily track every unpaid due, manage partial payments, and handle waivers to recover your money and keep your business moving forward.', bn: 'প্রতিটি অপরিশোধিত বকেয়া ট্র্যাক করুন এবং আপনার ব্যবসা এগিয়ে রাখুন।' },
  'lc-05': { en: 'Manage parts with AI driven categories, profit margin tracking, and automated low-stock alerts that notify you when it\'s time to restock.', bn: 'AI চালিত ক্যাটাগরি এবং স্বয়ংক্রিয় লো-স্টক অ্যালার্ট সহ পার্টস পরিচালনা করুন।' },
  'lc-06': { en: "Showcase your signature and specialized services with custom listings that highlight your shop's unique expertise.", bn: 'আপনার দোকানের অনন্য দক্ষতা তুলে ধরে কাস্টম তালিকা সহ আপনার বিশেষ সার্ভিস প্রদর্শন করুন।' },
  'lc-07': { en: 'Visual reports that reveal your most profitable parts, top-spending customers, and the success of your SMS campaigns. Instantly spot high-performing staff, track unpaid dues, and identify exactly where your shop is losing money.', bn: 'ভিজ্যুয়াল রিপোর্ট যা আপনার সবচেয়ে লাভজনক পার্টস এবং শীর্ষ গ্রাহকদের প্রকাশ করে।' },
  'lc-08': { en: 'Comprehensive AI profiles tracking every vehicle\'s history, unpaid dues, and profit potential to suggest the perfect parts for each rider.', bn: 'প্রতিটি বাইকের ইতিহাস ট্র্যাক করা ব্যাপক AI প্রোফাইল।' },
  'lc-09': { en: 'Visual charts comparing your daily income against fixed and variable costs to instantly pinpoint losses and balance your budget.', bn: 'আপনার দৈনিক আয়ের সাথে খরচ তুলনা করা ভিজ্যুয়াল চার্ট।' },
  'lc-10': { en: 'Unlock a new way to grow: send personalized maintenance alerts and targeted discount campaigns that bring customers back and keep your shop busy.', bn: 'ব্যক্তিগতকৃত রক্ষণাবেক্ষণ অ্যালার্ট এবং লক্ষ্যযুক্ত ডিসকাউন্ট ক্যাম্পেইন পাঠান।' },
  'lc-11': { en: 'Keep your shop safe by storing employee documents and profiles while easily handling their monthly salaries, bonuses, and deductions.', bn: 'কর্মচারী ডকুমেন্ট ও প্রোফাইল সংরক্ষণ করে আপনার দোকান নিরাপদ রাখুন।' },
  'lc-12': { en: "Boost your shop's reach with a smart points system and referral rewards that turn happy customers into a marketing team that shares your business for you.", bn: 'স্মার্ট পয়েন্ট সিস্টেম এবং রেফারেল রিওয়ার্ড দিয়ে আপনার দোকানের নাগাল বাড়ান।' },
  'lc-13': { en: 'Open new revenue streams by letting customers request rare, custom parts directly through your website, with instant notifications and messaging to close the sale fast.', bn: 'গ্রাহকদের আপনার ওয়েবসাইটের মাধ্যমে পার্টস অনুরোধ করতে দিয়ে নতুন রেভিনিউ স্ট্রিম খুলুন।' },
  'lc-14': { en: "Stop wasting hours on manual data entry—simply upload your supplier's list and let our AI instantly identify and import every item into your system with a single click.", bn: 'ম্যানুয়াল ডেটা এন্ট্রিতে সময় নষ্ট বন্ধ করুন — সাপ্লায়ারের তালিকা আপলোড করুন।' },
};

// ── Per-card font sizes (tuned to fit each card without clipping) ──
const CARD_FONT = {
  'lc-01': { title: 'text-[18px]', tag: 'text-[14px]', exp: 'text-[14px]' },
  'lc-02': { title: 'text-[14px]', tag: 'text-[12px]', exp: 'text-[11px]' },
  'lc-03': { title: 'text-[13.5px]', tag: 'text-[12px]', exp: 'text-[10.5px]' },
  'lc-04': { title: 'text-[13px]', tag: 'text-[11.5px]', exp: 'text-[10px]' },
  'lc-05': { title: 'text-[14px]', tag: 'text-[12px]', exp: 'text-[10.5px]' },
  'lc-06': { title: 'text-[14px]', tag: 'text-[12px]', exp: 'text-[10.5px]' },
  'lc-07': { title: 'text-[14px]', tag: 'text-[12.5px]', exp: 'text-[11px]' },
  'lc-08': { title: 'text-[13px]', tag: 'text-[11.5px]', exp: 'text-[10px]' },
  'lc-09': { title: 'text-[14px]', tag: 'text-[12px]', exp: 'text-[10.5px]' },
  'lc-10': { title: 'text-[14px]', tag: 'text-[12.5px]', exp: 'text-[11px]' },
  'lc-11': { title: 'text-[13px]', tag: 'text-[11.5px]', exp: 'text-[10px]' },
  'lc-12': { title: 'text-[14px]', tag: 'text-[12px]', exp: 'text-[10.5px]' },
  'lc-13': { title: 'text-[13px]', tag: 'text-[11.5px]', exp: 'text-[10px]' },
  'lc-14': { title: 'text-[14px]', tag: 'text-[12px]', exp: 'text-[10.5px]' },
};

// ── Multi-view map configurations ───────────────────
const MAP_VIEWS = [
  {
    id: 'dhaka',
    center: [23.80, 90.40],
    zoom: 11.5,
    label: { en: 'Dhaka', bn: 'ঢাকা' },
    subtitle: { en: 'Bangladesh', bn: 'বাংলাদেশ' },
    description: { en: 'Full city overview', bn: 'সম্পূর্ণ শহর' },
    areas: [
      { name: 'Airport', lat: 23.850, lng: 90.408 },
      { name: 'Kafrul', lat: 23.800, lng: 90.368 },
      { name: 'Tejgaon', lat: 23.770, lng: 90.395 },
      { name: 'Wari', lat: 23.720, lng: 90.408 },
      { name: 'Jatrabari', lat: 23.710, lng: 90.425 },
      { name: 'Keraniganj', lat: 23.698, lng: 90.385 },
      { name: 'Khilgaon', lat: 23.748, lng: 90.434 },
    ],
    pins: [
      { lat: 23.875, lng: 90.399 }, { lat: 23.793, lng: 90.415 },
      { lat: 23.747, lng: 90.375 }, { lat: 23.770, lng: 90.395 },
      { lat: 23.810, lng: 90.432 }, { lat: 23.735, lng: 90.410 },
      { lat: 23.855, lng: 90.370 }, { lat: 23.720, lng: 90.390 },
    ],
  },
  {
    id: 'uttara',
    center: [23.868, 90.397],
    zoom: 14,
    label: { en: 'Uttara', bn: 'উত্তরা' },
    subtitle: { en: 'North Dhaka', bn: 'উত্তর ঢাকা' },
    description: { en: 'Sector 1–14 coverage', bn: 'সেক্টর ১-১৪' },
    areas: [
      { name: 'Sector 7', lat: 23.872, lng: 90.393 },
      { name: 'Sector 10', lat: 23.865, lng: 90.389 },
      { name: 'Sector 3', lat: 23.876, lng: 90.400 },
      { name: 'Rajlokkhi', lat: 23.860, lng: 90.395 },
    ],
    pins: [
      { lat: 23.875, lng: 90.399 }, { lat: 23.869, lng: 90.391 },
      { lat: 23.864, lng: 90.398 }, { lat: 23.871, lng: 90.385 },
      { lat: 23.878, lng: 90.395 }, { lat: 23.862, lng: 90.402 },
    ],
  },
  {
    id: 'mirpur',
    center: [23.805, 90.362],
    zoom: 14,
    label: { en: 'Mirpur', bn: 'মিরপুর' },
    subtitle: { en: 'Central Dhaka', bn: 'কেন্দ্রীয় ঢাকা' },
    description: { en: 'Mirpur 1–14 coverage', bn: 'মিরপুর ১-১৪' },
    areas: [
      { name: 'Mirpur-1', lat: 23.797, lng: 90.355 },
      { name: 'Mirpur-10', lat: 23.808, lng: 90.368 },
      { name: 'Pallabi', lat: 23.822, lng: 90.365 },
      { name: 'Kafrul', lat: 23.800, lng: 90.378 },
    ],
    pins: [
      { lat: 23.800, lng: 90.358 }, { lat: 23.808, lng: 90.368 },
      { lat: 23.815, lng: 90.362 }, { lat: 23.795, lng: 90.372 },
      { lat: 23.822, lng: 90.358 }, { lat: 23.803, lng: 90.350 },
    ],
  },
];


// ── Rich expanded content per card ID ──────────────
function renderExpandedContent(card) {
  const id = card.id;
  switch (id) {
    case 'lc-01': // Job & Workflow Management (L)
      return (<>
        <div className="mp-job-pipe">
          <div className="mp-job-stage mp-stage-pending">Pending</div>
          <ChevronRight className="mp-job-arrow w-3.5 h-3.5" />
          <div className="mp-job-stage mp-stage-progress">In Progress</div>
          <ChevronRight className="mp-job-arrow w-3.5 h-3.5" />
          <div className="mp-job-stage mp-stage-ready">Ready</div>
          <ChevronRight className="mp-job-arrow w-3.5 h-3.5" />
          <div className="mp-job-stage mp-stage-deliver">Delivered</div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="bg-emerald-50 p-2 rounded-xl text-center">
            <div className="text-[9px] font-bold text-emerald-600 uppercase tracking-wide">Assign</div>
            <div className="text-sm font-[900] text-slate-900 mt-0.5">Technician</div>
          </div>
          <div className="bg-emerald-50 p-2 rounded-xl text-center">
            <div className="text-[9px] font-bold text-emerald-600 uppercase tracking-wide">Parts</div>
            <div className="text-sm font-[900] text-slate-900 mt-0.5">Inline Edit</div>
          </div>
          <div className="bg-emerald-50 p-2 rounded-xl text-center">
            <div className="text-[9px] font-bold text-emerald-600 uppercase tracking-wide">Quote PDF</div>
            <div className="text-sm font-[900] text-slate-900 mt-0.5">1-Click</div>
          </div>
        </div>
      </>);
    case 'lc-02': // Free Garage Website (S)
      return (<>
        <div className="flex flex-wrap gap-1 mt-2 justify-evenly">
          <span className="mp-plan-badge mp-badge-std" style={{ fontSize: '9.5px', padding: '2px 7px' }}>Branding</span>
          <span className="mp-plan-badge mp-badge-std" style={{ fontSize: '9.5px', padding: '2px 7px' }}>Location</span>
          <span className="mp-plan-badge mp-badge-std" style={{ fontSize: '9.5px', padding: '2px 7px' }}>Parts Requests</span>
        </div>
      </>);
    case 'lc-03': // Invoices & Quotations (S)
      return (<>
        <div className="flex gap-1 mt-1 flex-wrap justify-evenly">
          <span className="mp-plan-badge mp-badge-std">A4 PDF</span>
          <span className="mp-plan-badge mp-badge-prem">Thermal 58/80mm</span>
          <span className="mp-plan-badge mp-badge-std">WhatsApp</span>
        </div>
        <div className="flex gap-1 mt-1 justify-evenly">
          <span className="mp-plan-badge mp-badge-prem">3-yr Link</span>
          <span className="mp-plan-badge mp-badge-std">QR Code</span>
          <span className="mp-plan-badge mp-badge-prem">Auto-Send</span>
        </div>
      </>);
    case 'lc-04': // Dues Tracking (S)
      return (<>
        <div className="grid grid-cols-3 gap-1 mt-1.5">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-1 text-center">
            <div className="text-[7px] font-bold text-amber-600 uppercase tracking-wide">Total Due</div>
            <div className="text-[12px] font-[900] text-amber-700">৳ 32,450</div>
          </div>
          <div className="bg-slate-100 rounded-lg p-1 text-center">
            <div className="text-[7px] text-slate-500 font-bold uppercase tracking-wide">Defaulters</div>
            <div className="text-[12px] font-[900] text-slate-900">14</div>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-lg p-1 text-center">
            <div className="text-[7px] text-red-500 font-bold uppercase tracking-wide">Overdue</div>
            <div className="text-[12px] font-[900] text-red-500">3</div>
          </div>
        </div>
      </>);
    case 'lc-05': // Parts Inventory (S)
      return (<>
        <div className="space-y-1.5 mt-2">
          <div>
            <div className="flex justify-between text-[8.5px] font-bold text-slate-500 mb-0.5">
              <span>Brake Pads (SKU-441)</span><span className="text-emerald-600">12 left</span>
            </div>
            <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="mp-bar-fill" style={{ width: '60%' }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-[8.5px] font-bold text-slate-500 mb-0.5">
              <span>Engine Oil 20W-50</span><span className="text-red-500">2 left ⚠</span>
            </div>
            <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="mp-bar-fill" style={{ width: '12%' }} />
            </div>
          </div>
        </div>
        <div className="mt-1.5 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-emerald-600 flex-shrink-0" />
          <span className="text-[8px] font-bold text-emerald-600">Low-stock alerts & reorder suggestions</span>
        </div>
      </>);
    case 'lc-06': // Service Catalog (S)
      return (<>
        <div className="space-y-1 mt-2">
          <div className="flex items-center justify-between bg-slate-50 rounded-lg px-2 py-1">
            <span className="text-[9px] font-bold">Master Service</span>
            <span className="text-[8px] text-emerald-600 font-bold">৳ 2,500</span>
          </div>
          <div className="flex items-center justify-between bg-slate-50 rounded-lg px-2 py-1">
            <span className="text-[9px] font-bold">Exhaust Modify</span>
            <div className="flex items-center gap-1">
              <span className="text-[7px] text-slate-400 line-through">৳ 1,500</span>
              <span className="text-[8px] text-red-500 font-bold">৳ 1,200</span>
            </div>
          </div>
        </div>
      </>);
    case 'lc-07': // Revenue Reports & Visual Trends (W)
      return (<>
        <div className="flex items-end gap-1 mt-3 h-10">
          {[40, 55, 70, 65, 90, 50, 85].map((h, i) => (
            <div key={i} className="mp-spark-bar bg-emerald-600" style={{ height: `${h}%`, opacity: 0.3 + (h / 130), '--delay': `${0.3 + i * 0.08}s` }} />
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2 justify-evenly">
          <span className="mp-plan-badge mp-badge-prem">Daily/Weekly/Monthly</span>
          <span className="mp-plan-badge mp-badge-prem">Service Popularity</span>
          <span className="mp-plan-badge mp-badge-prem">Net Profit</span>
          <span className="mp-plan-badge mp-badge-prem">Top Parts</span>
          <span className="mp-plan-badge mp-badge-prem">Staff Performance</span>
          <span className="mp-plan-badge mp-badge-prem">Unpaid Dues</span>
          <span className="mp-plan-badge mp-badge-prem">SMS ROI</span>
        </div>
      </>);
    case 'lc-08': // Customer Profiles (S)
      return (<>
        <div className="grid grid-cols-3 gap-1 mt-1.5">
          <div className="bg-slate-100 rounded-lg px-1 py-0.5 text-center">
            <div className="text-[7.5px] text-slate-500 font-bold">Vehicles</div>
            <div className="text-[11px] font-[900] text-emerald-600">3</div>
          </div>
          <div className="bg-slate-100 rounded-lg px-1 py-0.5 text-center">
            <div className="text-[7.5px] text-slate-500 font-bold">Jobs</div>
            <div className="text-[11px] font-[900] text-emerald-600">18</div>
          </div>
          <div className="bg-slate-100 rounded-lg px-1 py-0.5 text-center">
            <div className="text-[7.5px] text-slate-500 font-bold">Due</div>
            <div className="text-[11px] font-[900] text-red-500">৳400</div>
          </div>
        </div>
        <div className="flex gap-1 mt-1.5 flex-wrap justify-evenly">
          <span className="mp-plan-badge mp-badge-prem" style={{ fontSize: '8.5px', padding: '1.5px 6px' }}>Loyalty Points</span>
          <span className="mp-plan-badge mp-badge-prem" style={{ fontSize: '8.5px', padding: '1.5px 6px' }}>Referrals</span>
          <span className="mp-plan-badge mp-badge-prem" style={{ fontSize: '8.5px', padding: '1.5px 6px' }}>Service History</span>
        </div>
      </>);
    case 'lc-09': // Income vs. Expense Analysis (T) — always-visible P&L + expanded breakdown
      return (<>
        <div className="bg-emerald-50/60 rounded-xl px-2.5 py-1.5 mt-2 border border-emerald-100">
          <div className="text-[8px] font-bold text-emerald-600 uppercase tracking-wide">Net Profit</div>
          <div className="text-lg font-[900] text-slate-900 leading-tight">৳ 52,700</div>
          <div className="text-[7.5px] text-emerald-600 font-bold">↑ 18% vs last month</div>
        </div>
        <div className="mt-2 space-y-1">
          <div className="flex justify-between text-[8.5px] font-bold">
            <span className="text-slate-500">Rent & Utilities</span><span>৳ 12,000</span>
          </div>
          <div className="flex justify-between text-[8.5px] font-bold">
            <span className="text-slate-500">Salaries</span><span>৳ 15,000</span>
          </div>
          <div className="flex justify-between text-[8.5px] font-bold">
            <span className="text-slate-500">Parts & Supplies</span><span>৳ 4,500</span>
          </div>
        </div>
      </>);
    case 'lc-10': // SMS Reminders & Campaigns (W)
      return (<>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div className="bg-slate-50 rounded-xl p-2">
            <div className="text-[10px] font-bold text-emerald-600">Auto Reminders</div>
            <div className="text-[10.5px] text-slate-500 mt-0.5">Service due alerts sent automatically based on mileage or date</div>
          </div>
          <div className="bg-slate-50 rounded-xl p-2">
            <div className="text-[10px] font-bold text-emerald-600">SMS Campaigns</div>
            <div className="text-[10.5px] text-slate-500 mt-0.5">Bulk SMS to filtered audiences — by last visit, vehicle type, or dues</div>
          </div>
        </div>
      </>);
    case 'lc-11': // HR & Payroll (S)
      return (<>
        <div className="flex gap-1 justify-evenly flex-wrap mt-2">
          <span className="mp-plan-badge mp-badge-prem" style={{ fontSize: '8.5px', padding: '1.5px 6px' }}>NID & Photo</span>
          <span className="mp-plan-badge mp-badge-prem" style={{ fontSize: '8.5px', padding: '1.5px 6px' }}>Roles</span>
          <span className="mp-plan-badge mp-badge-prem" style={{ fontSize: '8.5px', padding: '1.5px 6px' }}>Salary</span>
          <span className="mp-plan-badge mp-badge-prem" style={{ fontSize: '8.5px', padding: '1.5px 6px' }}>Advances</span>
        </div>
        <div className="bg-slate-100 rounded-xl p-2 mt-2">
          <div className="text-[8.5px] font-bold text-slate-500">This Month Payroll</div>
          <div className="text-sm font-[900] text-slate-900 mt-0.5">৳ 42,000</div>
          <div className="text-[8px] text-emerald-600 font-bold">4 employees · 1 advance</div>
        </div>
      </>);
    case 'lc-12': // Loyalty & Referrals (S)
      return (<>
        <div className="bg-emerald-50 rounded-xl p-2 mt-2">
          <div className="flex items-center gap-1.5">
            <Star className="w-3 h-3 text-emerald-600 fill-emerald-600" />
            <span className="text-[9px] font-[900] text-slate-900">Rahim Ahmed</span>
          </div>
          <div className="text-[9px] text-slate-500 mt-0.5">1,240 points · Redeemable: ৳124</div>
        </div>
      </>);
    case 'lc-13': // Parts Request Inbox (S)
      return (<>
        <div className="space-y-1.5 mt-2">
          <div className="flex items-center gap-2 bg-emerald-50 rounded-lg px-2 py-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
            <span className="text-[9px] font-bold flex-1">Air Filter — Honda CB150</span>
            <span className="text-[8px] text-slate-400">2m ago</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-2 py-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
            <span className="text-[9px] font-bold flex-1">Brake Cable — Yamaha FZS</span>
            <span className="text-[8px] text-slate-400">1h ago</span>
          </div>
        </div>
      </>);
    case 'lc-14': // AI Inventory Import (S)
      return (<>
        <div className="flex flex-wrap gap-1 mt-2 justify-evenly">
          <span className="mp-plan-badge mp-badge-prem">CSV</span>
          <span className="mp-plan-badge mp-badge-prem">PDF</span>
          <span className="mp-plan-badge mp-badge-prem">Image</span>
          <span className="mp-plan-badge mp-badge-prem">Doc</span>
          <span className="mp-plan-badge mp-badge-prem">Excel</span>
        </div>
      </>);
    default:
      // Fallback: show tags if available
      return card.tags?.length > 0 ? (
        <div className="flex flex-wrap gap-1 mt-1">
          {card.tags.map((tag, i) => (
            <span key={i} className={`mp-plan-badge ${card.plan_tier === 'premium' ? 'mp-badge-prem' : 'mp-badge-std'}`}>{tag}</span>
          ))}
        </div>
      ) : null;
  }
}

// ── CountUp animation component ────────────────────
// Supports pure numbers AND text like "43L", "15K", "24/7", "99.9%", "4.8★"
function CountUp({ end, duration = 1.6, suffix: explicitSuffix }) {
  const ref = useRef(null);
  const animatedEnd = useRef(null);
  const [value, setValue] = useState(0);
  // Parse numeric prefix and text suffix from values like "43L", "15K", "99.9%"
  const parsed = useMemo(() => {
    const str = String(end);
    const m = str.match(/^([\d.,]+)(.*)/);
    if (m) return { num: parseFloat(m[1].replace(/,/g, '')), suffix: m[2] + (explicitSuffix || ''), decimals: (m[1].split('.')[1] || '').length };
    return { num: 0, suffix: str + (explicitSuffix || ''), decimals: 0, noAnimate: true };
  }, [end, explicitSuffix]);

  useEffect(() => {
    const el = ref.current;
    if (!el || parsed.noAnimate) return;
    // Skip if we already animated this exact end value
    if (animatedEnd.current === end) return;
    const animate = () => {
      animatedEnd.current = end;
      const start = performance.now();
      const step = (now) => {
        const t = Math.min((now - start) / (duration * 1000), 1);
        const ease = 1 - Math.pow(1 - t, 3);
        setValue(parsed.decimals > 0 ? +(ease * parsed.num).toFixed(parsed.decimals) : Math.round(ease * parsed.num));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        animate();
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration, parsed]);
  if (parsed.noAnimate) return <span ref={ref}>{parsed.suffix}</span>;
  return <span ref={ref}>{parsed.decimals > 0 ? value.toFixed(parsed.decimals) : value.toLocaleString()}{parsed.suffix}</span>;
}

// AnimatedPrice — smoothly animates between price values on change
function AnimatedPrice({ value, duration = 600 }) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(value);

  useEffect(() => {
    if (fromRef.current === value) return;
    const from = fromRef.current;
    const to = value;
    fromRef.current = value;
    startRef.current = performance.now();
    const animate = (now) => {
      const elapsed = now - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value, duration]);

  return <span>{display.toLocaleString()}</span>;
}

// ── Pricing Section (extracted for mobile reorder) ────────────
function PricingSection({ language, billingCycle, setBillingCycle, stdPricing, premPricing, standardFeatures, premiumFeatures, setSubscribeForm, setShowSubscribeForm }) {
  return (
    <section id={`pricing`} className="py-20 sm:py-28 px-4 sm:px-8 bg-slate-50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-6 mp-reveal">
          <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-bold tracking-wider uppercase px-4 py-1.5">
            {language === 'en' ? 'Subscription Pricing' : 'সাবস্ক্রিপশন মূল্য'}
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-[900] font-['Epilogue'] text-slate-900 tracking-tight mb-4">
            {language === 'en' ? 'Simple Pricing. Serious Value.' : 'সহজ মূল্য। গুরুতর মূল্য।'}
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-lg">
            {language === 'en'
              ? 'Pick the plan that fits your garage. No hidden fees. Pay longer, save more.'
              : 'আপনার গ্যারেজের জন্য সঠিক প্ল্যান বেছে নিন। কোনো লুকানো ফি নেই।'}
          </p>
        </div>

        {/* Billing toggle */}
        <div className="flex justify-center mb-12 mp-reveal">
          <div className="relative bg-slate-100 rounded-full p-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
            <div
              className="absolute top-1 bottom-1 bg-white rounded-full shadow-md transition-all duration-300 ease-out"
              style={{
                left: billingCycle === 'monthly' ? '4px' : billingCycle === 'half-yearly' ? 'calc(33.33% + 1px)' : 'calc(66.66% - 1px)',
                width: 'calc(33.33% - 3px)',
              }}
            />
            {[
              { key: 'monthly', label: language === 'en' ? 'Monthly' : 'মাসিক' },
              { key: 'half-yearly', label: language === 'en' ? 'Half-Yearly' : 'অর্ধ-বার্ষিক', badge: language === 'en' ? '1 month FREE' : '১ মাস ফ্রি' },
              { key: 'yearly', label: language === 'en' ? 'Yearly' : 'বার্ষিক', badge: language === 'en' ? '3 months FREE' : '৩ মাস ফ্রি' },
            ].map(b => (
              <button
                key={b.key}
                onClick={() => setBillingCycle(b.key)}
                className={`relative z-10 px-4 sm:px-6 py-2.5 rounded-full text-sm font-bold transition-colors duration-300 whitespace-nowrap text-center ${billingCycle === b.key ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {b.label}
                {b.badge && billingCycle === b.key && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap animate-[fadeSlideIn_0.3s_ease-out]">{b.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mp-reveal">
          {[
            { key: 'standard', label: 'Standard', pricing: stdPricing, features: standardFeatures, isPremium: false },
            { key: 'premium', label: 'Premium', pricing: premPricing, features: premiumFeatures, isPremium: true },
          ].map(({ key: planKey, label, pricing, features, isPremium }) => {
            const cycleData = pricing[billingCycle === 'half-yearly' ? 'half-yearly' : billingCycle];
            const totalPrice = cycleData?.discounted || 0;
            const months = billingCycle === 'yearly' ? 12 : billingCycle === 'half-yearly' ? 6 : 1;
            const perMonth = Math.round(totalPrice / months);
            const regularTotal = billingCycle === 'monthly' ? pricing.monthly.regular * 1 : billingCycle === 'half-yearly' ? pricing.monthly.regular * 6 : pricing.monthly.regular * 12;
            const savings = regularTotal - totalPrice;
            const cycleLabel = billingCycle === 'half-yearly' ? (language === 'en' ? '6 months' : '৬ মাস') : billingCycle === 'yearly' ? (language === 'en' ? '1 year' : '১ বছর') : (language === 'en' ? '1 month' : '১ মাস');

            return (
              <div key={planKey} className={`mp-pricing-card bg-white rounded-3xl ${isPremium ? 'border-2 border-emerald-500 shadow-2xl shadow-emerald-100/50' : 'border border-slate-200'} p-8 lg:p-10 flex flex-col relative`}>
                {isPremium && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-5 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 shadow-lg shadow-emerald-600/30">
                    <Star className="w-3 h-3 fill-current" />{language === 'en' ? 'Most Popular' : 'সবচেয়ে জনপ্রিয়'}
                  </div>
                )}
                <h3 className="text-2xl font-[800] font-['Epilogue'] text-slate-900 mb-2">{label}</h3>
                <p className="text-sm text-slate-500 mb-6">{language === 'en' ? pricing.tagline : pricing.tagline_bn}</p>

                <div className="mb-2">
                  <div className="flex items-baseline gap-1">
                    <span className="text-emerald-600 text-lg font-bold">৳</span>
                    <span className="text-5xl font-[900] font-['Epilogue'] text-slate-900">
                      <AnimatedPrice value={perMonth} />
                    </span>
                    <span className="text-slate-400 text-sm font-medium">/{language === 'en' ? 'mo' : 'মাস'}</span>
                  </div>
                </div>

                <div className="mb-6 transition-all duration-500 ease-out overflow-hidden" style={{ maxHeight: billingCycle !== 'monthly' || pricing.monthly.regular > pricing.monthly.discounted ? '80px' : '0px', opacity: billingCycle !== 'monthly' || pricing.monthly.regular > pricing.monthly.discounted ? 1 : 0 }}>
                  {billingCycle !== 'monthly' && (
                    <div key={billingCycle} className="animate-[fadeSlideIn_0.3s_ease-out]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-lg font-[800] text-slate-700">৳{totalPrice.toLocaleString()}</span>
                        <span className="text-sm text-slate-400 font-medium">{language === 'en' ? 'for' : 'জন্য'} {cycleLabel}</span>
                      </div>
                      {savings > 0 && (
                        <div className="mt-1.5">
                          <span className="bg-emerald-100 text-emerald-700 text-sm font-bold px-3 py-1 rounded-full inline-flex items-center gap-1">
                            <Sparkles className="w-3.5 h-3.5" />
                            {language === 'en' ? `Save ৳${savings.toLocaleString()}` : `সাশ্রয় ৳${savings.toLocaleString()}`}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {billingCycle === 'monthly' && pricing.monthly.regular > pricing.monthly.discounted && (
                    <div className="animate-[fadeSlideIn_0.3s_ease-out]">
                      <span className="text-slate-400 text-sm line-through">৳{pricing.monthly.regular.toLocaleString()}/{language === 'en' ? 'mo' : 'মাস'}</span>
                    </div>
                  )}
                </div>

                <div className="mt-2">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-4">
                    {isPremium
                      ? (language === 'en' ? (<>Everything in <span className="text-emerald-600">Standard</span>, <span className="text-emerald-600 font-[900]">plus</span>:</>) : (<><span className="text-emerald-600">Standard</span>-এর সবকিছু, <span className="text-emerald-600 font-[900]">এবং</span>:</>))
                      : (language === 'en' ? "What's included" : 'যা অন্তর্ভুক্ত')
                    }
                  </p>
                  <ul className="space-y-3 mb-6">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm"><CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span className="text-slate-700">{language === 'bn' ? f.bn : f.en}</span></li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto pt-6">
                  <Button
                    className={`w-full h-12 text-base font-bold rounded-xl ${isPremium ? 'bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/20' : 'border-2 border-slate-200 hover:border-emerald-500 hover:text-emerald-600'}`}
                    variant={isPremium ? 'default' : 'outline'}
                    onClick={() => { setSubscribeForm(f => ({ ...f, plan: planKey, billing_cycle: billingCycle })); setShowSubscribeForm(true); }}
                  >
                    {language === 'en' ? 'Get Started' : 'শুরু করুন'}{isPremium && <ArrowRight className="ml-2 w-4 h-4" />}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════
export default function MotoPottiLandingPageV2() {
  const { language, toggleLanguage } = useLanguage();
  const { toast } = useToast();

  const [featureCards, setFeatureCards] = useState([]);
  const [garages, setGarages] = useState([]);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [landingStats, setLandingStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Bento state
  const [activeCard, setActiveCard] = useState(null);
  const gridRef = useRef(null);
  const hoverTimer = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapMarkersRef = useRef([]);
  const [activeMapIndex, setActiveMapIndex] = useState(0);
  const mapTransitioning = useRef(false);
  const [isMobile, setIsMobile] = useState(false);
  const mapAutoScrollRef = useRef(null);
  const [mapAutoPaused, setMapAutoPaused] = useState(false);
  const [featureProgress, setFeatureProgress] = useState(0);
  const [showFeatureProgress, setShowFeatureProgress] = useState(false);
  const featureSectionRef = useRef(null);

  // Dialogs
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [showSubscribeForm, setShowSubscribeForm] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState('');
  const [showSubPassword, setShowSubPassword] = useState(false);
  const [subFormErrors, setSubFormErrors] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly | half-yearly | yearly
  const [activeSection, setActiveSection] = useState('');  // for nav highlight
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [confettiPieces, setConfettiPieces] = useState([]);
  const [copiedNumber, setCopiedNumber] = useState(false);
  const [subStep, setSubStep] = useState(1); // multi-step form: 1=garage info, 2=plan+payment

  // Voucher state
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherApplied, setVoucherApplied] = useState(null); // { code, discount_percent, plan_restriction }
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState('');

  const [demoForm, setDemoForm] = useState({ garage_name: '', contact_name: '', email: '', phone: '', message: '' });
  const [subscribeForm, setSubscribeForm] = useState({
    garage_name: '', garage_name_bn: '', owner_name: '', email: '',
    password: '', phone: '', address: '', address_bn: '',
    plan: 'standard', billing_cycle: 'monthly',
    payment_method: 'bkash', transaction_id: '', payment_phone: '',
    voucher_code: '', free_trial: false
  });

  // ── Fetch data ───────────────────────────────────
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/landing/cards`).catch(() => ({ data: [] })),
      axios.get(`${API}/tenants/public`).catch(() => ({ data: [] })),
      axios.get(`${API}/pricing`).catch(() => ({ data: { plans: [] } })),
      axios.get(`${API}/landing/stats`).catch(() => ({ data: null })),
    ]).then(([cardsRes, garagesRes, pricingRes, statsRes]) => {
      setFeatureCards(cardsRes.data);
      setGarages(garagesRes.data);
      if (pricingRes.data?.plans?.length) setPricingPlans(pricingRes.data.plans);
      if (statsRes.data) setLandingStats(statsRes.data);
    });
  }, []);

  // ── Measure bento card cells and set --cell-h before paint ────
  useLayoutEffect(() => {
    if (!gridRef.current || featureCards.length === 0) return;
    const measure = () => {
      gridRef.current.querySelectorAll('.mp-bento-card').forEach(card => {
        card.style.setProperty('--cell-h', card.getBoundingClientRect().height + 'px');
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [featureCards]);

  // ── Initialize Leaflet map ────
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const view = MAP_VIEWS[0];
    const map = L.map(mapRef.current, {
      center: view.center,
      zoom: view.zoom,
      scrollWheelZoom: false,
      zoomControl: false,
      attributionControl: false,
    });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
    }).addTo(map);
    mapInstanceRef.current = map;

    // Add initial view markers
    const addMarkers = (viewData) => {
      // Clear old markers
      mapMarkersRef.current.forEach(m => m.remove());
      mapMarkersRef.current = [];
      // Area labels
      viewData.areas.forEach(area => {
        const marker = L.marker([area.lat, area.lng], {
          icon: L.divIcon({
            className: '',
            html: `<span style="font-size:8px;font-weight:600;color:rgba(71,85,105,0.55);letter-spacing:0.06em;text-transform:uppercase;font-family:'Plus Jakarta Sans',sans-serif;white-space:nowrap;pointer-events:none">${area.name}</span>`,
            iconSize: [0, 0],
            iconAnchor: [-8, 3],
          }),
          interactive: false,
        }).addTo(map);
        mapMarkersRef.current.push(marker);
      });
      // Pins
      viewData.pins.forEach((loc, i) => {
        const delay = ((i * 0.6) % 2.5).toFixed(1);
        const icon = L.divIcon({
          className: '',
          html: `<div class="mp-lf-pin"><div class="lf-pulse2" style="animation-delay:${delay}s"></div><div class="lf-pulse" style="animation-delay:${delay}s"></div><div class="lf-core"></div></div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        });
        const marker = L.marker([loc.lat, loc.lng], { icon }).addTo(map);
        mapMarkersRef.current.push(marker);
      });
    };
    addMarkers(view);

    // Store addMarkers fn for later use
    map._mpAddMarkers = addMarkers;

    return () => { map.remove(); mapInstanceRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fly to new map view when activeMapIndex changes ────
  const mapInitDone = useRef(false);
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    // Skip on initial mount — map init already sets view 0
    if (!mapInitDone.current) { mapInitDone.current = true; return; }
    const view = MAP_VIEWS[activeMapIndex];
    mapTransitioning.current = true;
    // Fade out markers first
    mapMarkersRef.current.forEach(m => {
      const el = m.getElement?.();
      if (el) { el.style.transition = 'opacity 0.3s'; el.style.opacity = '0'; }
    });
    setTimeout(() => {
      map.flyTo(view.center, view.zoom, { duration: 1.2, easeLinearity: 0.35 });
    }, 200);
    const onMoveEnd = () => {
      map.off('moveend', onMoveEnd);
      if (map._mpAddMarkers) map._mpAddMarkers(view);
      // Fade in new markers
      setTimeout(() => {
        mapMarkersRef.current.forEach(m => {
          const el = m.getElement?.();
          if (el) { el.style.transition = 'opacity 0.4s'; el.style.opacity = '1'; }
        });
        mapTransitioning.current = false;
      }, 100);
    };
    map.once('moveend', onMoveEnd);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMapIndex]);

  // ── Map auto-scroll (all views) ────
  const resetMapAutoScroll = useCallback(() => {
    if (mapAutoScrollRef.current) clearInterval(mapAutoScrollRef.current);
    setMapAutoPaused(false);
    mapAutoScrollRef.current = setInterval(() => {
      if (mapTransitioning.current) return;
      setActiveMapIndex(prev => (prev + 1) % MAP_VIEWS.length);
    }, 5000);
  }, []);

  const toggleMapAutoScroll = useCallback(() => {
    if (mapAutoPaused) {
      resetMapAutoScroll();
    } else {
      if (mapAutoScrollRef.current) clearInterval(mapAutoScrollRef.current);
      setMapAutoPaused(true);
    }
  }, [mapAutoPaused, resetMapAutoScroll]);

  useEffect(() => {
    resetMapAutoScroll();
    return () => { if (mapAutoScrollRef.current) clearInterval(mapAutoScrollRef.current); };
  }, [resetMapAutoScroll]);

  // ── Wheel handler for map scroll-jacking (DISABLED — auto-rotate + click handles navigation) ────
  // useEffect removed: scroll no longer changes map view.

  // ── Mobile detection ────
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 639);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── Mobile card entrance animation ────
  useEffect(() => {
    if (!isMobile || featureCards.length === 0) return;
    const cards = document.querySelectorAll('.mp-bento-card');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('mp-mob-visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' });
    cards.forEach(c => obs.observe(c));
    return () => obs.disconnect();
  }, [isMobile, featureCards]);

  // ── Ensure cards visible on desktop (cleanup mobile class) ────
  useEffect(() => {
    if (isMobile) return;
    document.querySelectorAll('.mp-bento-card').forEach(c => c.classList.add('mp-mob-visible'));
  }, [isMobile, featureCards]);

  // ── Feature section scroll progress (mobile only) ────
  useEffect(() => {
    if (!isMobile) { setShowFeatureProgress(false); return; }
    const section = featureSectionRef.current;
    if (!section) return;
    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const viewportH = window.innerHeight;
      // Show progress bar when section is in view
      if (sectionTop < viewportH && sectionTop + sectionHeight > 0) {
        setShowFeatureProgress(true);
        // Calculate how much of the section has been scrolled through
        const scrolled = Math.max(0, viewportH - sectionTop);
        const totalScrollable = sectionHeight + viewportH;
        const pct = Math.min(100, Math.max(0, (scrolled / totalScrollable) * 100));
        setFeatureProgress(pct);
      } else {
        setShowFeatureProgress(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // ── Add real tenant pins when garages data arrives ────
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const tenants = garages.filter(g => g.latitude && g.longitude);
    if (tenants.length === 0) return;
    const map = mapInstanceRef.current;
    tenants.forEach((loc, i) => {
      const delay = ((i * 0.6) % 2.5).toFixed(1);
      const icon = L.divIcon({
        className: '',
        html: `<div class="mp-lf-pin"><div class="lf-pulse2" style="animation-delay:${delay}s"></div><div class="lf-pulse" style="animation-delay:${delay}s"></div><div class="lf-core"></div></div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      const marker = L.marker([loc.latitude, loc.longitude], { icon });
      if (loc.name) marker.bindTooltip(loc.name, { direction: 'top', offset: [0, -8] });
      marker.addTo(map);
    });
  }, [garages]);

  // ── Intersection observer for section reveals ────
  useEffect(() => {
    const els = document.querySelectorAll('.mp-reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.15 });
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [featureCards, garages]);

  // ── Active section observer for nav highlight ────
  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
    }, { rootMargin: '-40% 0px -55% 0px' });
    sections.forEach(s => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  // ── Bento hover logic ────────────────────────────
  const getOrigin = useCallback((cardEl) => {
    if (!gridRef.current) return 'top left';
    const gR = gridRef.current.getBoundingClientRect();
    const cR = cardEl.getBoundingClientRect();
    const relL = cR.left - gR.left, relR = gR.right - cR.right;
    const relT = cR.top - gR.top, relB = gR.bottom - cR.bottom;
    const h = relL < gR.width * 0.28 ? 'left' : relR < gR.width * 0.28 ? 'right' : 'center';
    const v = relT < gR.height * 0.32 ? 'top' : relB < gR.height * 0.32 ? 'bottom' : 'center';
    return `${v} ${h}`;
  }, []);

  const expandCard = useCallback((idx, el) => {
    if (activeCard === idx || !el) return;
    const cellH = el.getBoundingClientRect().height;
    const card = featureCards[idx];
    const cfg = SIZE_CFG[card?.grid_size] || SIZE_CFG.S;
    const cardOverride = CARD_EXTRA[card?.id];
    const extra = cardOverride?.extra ?? cfg.extra;
    el.style.setProperty('--cell-h', cellH + 'px');
    el.style.setProperty('--exp-h', (cellH + extra) + 'px');
    el.style.setProperty('--scale', cfg.scale);
    el.style.setProperty('--scale', cfg.scale);
    el.style.setProperty('--origin', getOrigin(el));
    setActiveCard(idx);
  }, [activeCard, featureCards, getOrigin]);

  const collapseCards = useCallback(() => setActiveCard(null), []);

  // ── Form handlers ────────────────────────────────
  const submitDemoRequest = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/demo-requests`, demoForm);
      toast({ title: language === 'en' ? 'Demo request submitted!' : 'ডেমো অনুরোধ জমা হয়েছে!' });
      setShowDemoForm(false);
      setDemoForm({ garage_name: '', contact_name: '', email: '', phone: '', message: '' });
    } catch {
      toast({ title: 'Failed to submit', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const validateSubForm = () => {
    const errors = {};
    if (!subscribeForm.garage_name.trim()) errors.garage_name = true;
    if (!subscribeForm.owner_name.trim()) errors.owner_name = true;
    if (!subscribeForm.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subscribeForm.email.trim())) errors.email = true;
    if (!subscribeForm.phone.trim() || !/^01\d{9}$/.test(subscribeForm.phone.trim())) errors.phone = true;
    if (!subscribeForm.password || subscribeForm.password.length < 6) errors.password = true;
    if (!subscribeForm.address.trim()) errors.address = true;
    setSubFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Voucher handlers ─────────────────────────────
  const applyVoucher = async () => {
    if (!voucherCode.trim()) return;
    setVoucherLoading(true);
    setVoucherError('');
    try {
      const res = await axios.post(`${API}/vouchers/validate`, {
        code: voucherCode.trim().toUpperCase(),
        plan: subscribeForm.plan,
      });
      setVoucherApplied({ ...res.data, code: voucherCode.trim().toUpperCase(), plan_restriction: res.data.plan });
      setSubscribeForm(f => ({ ...f, voucher_code: voucherCode.trim().toUpperCase() }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setVoucherError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Invalid voucher');
      setVoucherApplied(null);
      setSubscribeForm(f => ({ ...f, voucher_code: '' }));
    } finally { setVoucherLoading(false); }
  };

  const removeVoucher = () => {
    setVoucherApplied(null);
    setVoucherCode('');
    setVoucherError('');
    setSubscribeForm(f => ({ ...f, voucher_code: '' }));
  };

  const submitSubscription = async () => {
    if (!validateSubForm()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscriptions`, { ...subscribeForm, free_trial: subscribeForm.free_trial });
      setShowSubscribeForm(false);
      setSubStep(1);

      // Calculate billing info for success popup
      const pricing = subscribeForm.plan === 'premium' ? premPricing : stdPricing;
      const cycle = subscribeForm.billing_cycle;
      const rawAmount = pricing[cycle === 'half-yearly' ? 'half-yearly' : cycle]?.discounted || 0;
      const baseAmount = subscribeForm.free_trial ? 0 : rawAmount;
      const discountAmt = (!subscribeForm.free_trial && voucherApplied) ? Math.round(rawAmount * voucherApplied.discount_percent / 100) : 0;
      const finalAmount = subscribeForm.free_trial ? 0 : rawAmount - discountAmt;
      const cycleDays = cycle === 'yearly' ? 365 : cycle === 'half-yearly' ? 180 : 30;
      const trialDays = subscribeForm.free_trial ? 30 : 0;
      const startDate = new Date();
      const nextBillDate = new Date(startDate.getTime() + (cycleDays + trialDays) * 24 * 60 * 60 * 1000);

      setSuccessData({
        slug: response.data.slug,
        plan: subscribeForm.plan,
        billing_cycle: cycle,
        amount: finalAmount,
        originalAmount: voucherApplied ? rawAmount : null,
        voucherCode: voucherApplied?.code || null,
        voucherDiscount: voucherApplied?.discount_percent || null,
        discountAmount: discountAmt || null,
        freeTrial: subscribeForm.free_trial,
        startDate: startDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
        nextBillDate: nextBillDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
      });

      // Reset voucher state
      removeVoucher();

      // Trigger confetti
      const pieces = Array.from({ length: 60 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.8,
        duration: 2 + Math.random() * 2,
        color: ['#10b981', '#059669', '#34d399', '#6ee7b7', '#064e3b', '#0d9488', '#14b8a6', '#ffffff'][Math.floor(Math.random() * 8)],
        size: 6 + Math.random() * 8,
        rotation: Math.random() * 360,
      }));
      setConfettiPieces(pieces);
      setShowSuccessPopup(true);
      setTimeout(() => setConfettiPieces([]), 4000);
    } catch (err) {
      console.error('Subscription error:', err.response?.data);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ') : 'Failed to submit';
      toast({ title: msg, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  // ── Pricing data (from API with fallbacks) ─────
  const PAYMENT_NUMBER = '01786264886';
  const copyPaymentNumber = () => {
    navigator.clipboard.writeText(PAYMENT_NUMBER);
    setCopiedNumber(true);
    setTimeout(() => setCopiedNumber(false), 2000);
  };

  const stdPlan = pricingPlans.find(p => p.id === 'standard');
  const premPlan = pricingPlans.find(p => p.id === 'premium');

  const standardFeatures = useMemo(() => {
    if (stdPlan?.features?.length) {
      return stdPlan.features.map((f, i) => ({
        en: f,
        bn: stdPlan.features_bn?.[i] || f
      }));
    }
    return [
      { en: 'Free Garage Website with Branding, Service & Parts Requests', bn: 'ব্র্যান্ডিং, সার্ভিস ও পার্টস রিকোয়েস্ট সহ ফ্রি গ্যারেজ ওয়েবসাইট' },
      { en: 'End-to-End Job & Workflow Management', bn: 'সম্পূর্ণ জব ও ওয়ার্কফ্লো ম্যানেজমেন্ট' },
      { en: 'Extensive Customer & Vehicle Management', bn: 'বিস্তৃত গ্রাহক ও বাইক ম্যানেজমেন্ট' },
      { en: 'Organized Parts Inventory System', bn: 'সুসংগঠিত পার্টস ইনভেন্টরি সিস্টেম' },
      { en: 'Professional Service Catalog Setup & Display', bn: 'পেশাদার সার্ভিস ক্যাটালগ সেটআপ ও ডিসপ্লে' },
      { en: 'A4 Quotation/Invoice PDF + Share via Any App', bn: 'A4 কোটেশন/ইনভয়েস PDF + যেকোনো অ্যাপে শেয়ার' },
      { en: 'Dues Tracking & Management', bn: 'বকেয়া ট্র্যাকিং ও ম্যানেজমেন্ট' },
      { en: 'Basic Revenue Reports', bn: 'বেসিক রেভিনিউ রিপোর্ট' },
      { en: 'Staff Management & Roles', bn: 'স্টাফ ম্যানেজমেন্ট ও রোল' },
      { en: 'Bilingual UI (EN / BN)', bn: 'দ্বিভাষিক UI (EN / BN)' },
    ];
  }, [stdPlan]);

  const premiumFeatures = useMemo(() => {
    if (premPlan?.features?.length) {
      return premPlan.features.map((f, i) => ({
        en: f,
        bn: premPlan.features_bn?.[i] || f
      }));
    }
    return [
      { en: 'Visual Revenue Trends & Deep Profit Insights', bn: 'ভিজ্যুয়াল রেভিনিউ ট্রেন্ড ও গভীর লাভ বিশ্লেষণ' },
      { en: 'Job Performance Analytics & Service Demand Insights', bn: 'জব পারফরম্যান্স অ্যানালিটিক্স ও সার্ভিস ডিমান্ড ইনসাইট' },
      { en: 'Smart Expense Tracking with Detailed Cost Breakdown', bn: 'স্মার্ট খরচ ট্র্যাকিং বিস্তারিত খরচ বিশ্লেষণ সহ' },
      { en: 'Employee Profiles, NID & Payroll', bn: 'কর্মচারী প্রোফাইল, NID ও পে-রোল' },
      { en: 'Salary Advances, Bonuses & Deductions', bn: 'বেতন অগ্রিম, বোনাস ও কর্তন' },
      { en: 'Loyalty Points & Referral Program', bn: 'লয়্যালটি পয়েন্ট ও রেফারেল প্রোগ্রাম' },
      { en: 'SMS Campaigns with Audience Filters', bn: 'অডিয়েন্স ফিল্টার সহ SMS ক্যাম্পেইন' },
      { en: 'Smart AI Service Reminders', bn: 'স্মার্ট AI সার্ভিস রিমাইন্ডার' },
      { en: 'Batch Add Parts (CSV / PDF / Image)', bn: 'ব্যাচ পার্টস যোগ (CSV / PDF / ছবি)' },
      { en: 'Split & Second Payment Support', bn: 'স্প্লিট ও সেকেন্ড পেমেন্ট সাপোর্ট' },
      { en: 'Thermal PDF (58mm & 80mm)', bn: 'থার্মাল PDF (৫৮মিমি ও ৮০মিমি)' },
      { en: '3-Year Permanent Share Links', bn: '৩-বছর স্থায়ী শেয়ার লিংক' },
      { en: 'Parts Request Inbox & Auto-Queue Reminders', bn: 'পার্টস রিকোয়েস্ট ইনবক্স ও অটো-কিউ রিমাইন্ডার' },
    ];
  }, [premPlan]);

  // Pricing helpers — use API data with fallbacks
  const stdPricing = {
    monthly: { regular: stdPlan?.regular_monthly_price || 1800, discounted: stdPlan?.discounted_monthly_price || stdPlan?.monthly_price || 1800 },
    'half-yearly': { regular: stdPlan?.regular_half_yearly_price || 10800, discounted: stdPlan?.discounted_half_yearly_price || stdPlan?.half_yearly_price || 9000 },
    yearly: { regular: stdPlan?.regular_yearly_price || 21600, discounted: stdPlan?.discounted_yearly_price || stdPlan?.yearly_price || 16200 },
    tagline: stdPlan?.tagline || 'Great for growing garages',
    tagline_bn: stdPlan?.tagline_bn || 'বৃদ্ধিশীল গ্যারেজের জন্য',
  };
  const premPricing = {
    monthly: { regular: premPlan?.regular_monthly_price || 3600, discounted: premPlan?.discounted_monthly_price || premPlan?.monthly_price || 3600 },
    'half-yearly': { regular: premPlan?.regular_half_yearly_price || 21600, discounted: premPlan?.discounted_half_yearly_price || premPlan?.half_yearly_price || 18000 },
    yearly: { regular: premPlan?.regular_yearly_price || 43200, discounted: premPlan?.discounted_yearly_price || premPlan?.yearly_price || 32400 },
    tagline: premPlan?.tagline || 'For serious, full-featured operations',
    tagline_bn: premPlan?.tagline_bn || 'সিরিয়াস, পূর্ণ-ফিচার অপারেশনের জন্য',
  };


  return (
    <div className="mp-brand min-h-screen bg-slate-50 font-['Plus_Jakarta_Sans']">
      <style>{BENTO_STYLES}</style>
      <style>{`
        /* ── MotoPotti V2 brand palette (final) ─────────────────────────
           Two-color system, scoped to .mp-brand:
             • PRIMARY  #013C10   deep forest — heavy weight, headlines depth
             • ACCENT   #009137   pure brand green — accent text, icons, dots,
                                  pricing checks, "৳", small numbers, "Standard"
             • CTA      linear-gradient(135deg, #013C10 → #009137)
                                  applied to ALL primary buttons (Start Free
                                  Trial, Get Started, Pricing CTAs, "popular"
                                  badge). Reads as a confident depth-to-light
                                  sweep instead of a flat block.
             • Surfaces 8–16% accent — mint wash on chips/cards
             • Hover    darker stops to keep contrast on press
        */

        /* ─── Accent text (small bright details) ─────────────────────── */
        .mp-brand .text-emerald-500 { color: #009137 !important; }
        .mp-brand .text-emerald-600 { color: #009137 !important; }
        .mp-brand .hover\\:text-emerald-600:hover { color: #013C10 !important; }
        .mp-brand .fill-emerald-500 { fill: #009137 !important; }
        .mp-brand .fill-emerald-600 { fill: #009137 !important; }

        /* ─── Primary text (deep emphasis) ───────────────────────────── */
        .mp-brand .text-emerald-700 { color: #013C10 !important; }

        /* ─── Solid green buttons → upgraded to brand gradient ───────── */
        /* bg-emerald-600 is used by the nav "Start Free Trial" button and a
           few other non-gradient CTAs. We promote them all to the brand
           gradient so they match the hero/pricing CTAs. */
        .mp-brand .bg-emerald-600 {
          background-color: #013C10 !important;
          background-image: linear-gradient(135deg, #013C10 0%, #009137 100%) !important;
        }
        .mp-brand .hover\\:bg-emerald-700:hover {
          background-color: #002a08 !important;
          background-image: linear-gradient(135deg, #002a08 0%, #007a2e 100%) !important;
        }

        /* ─── Tailwind gradient classes (from-emerald-600 to-emerald-500) ─── */
        .mp-brand .from-emerald-600 {
          --tw-gradient-from: #013C10 var(--tw-gradient-from-position) !important;
          --tw-gradient-to: rgba(1,60,16,0) var(--tw-gradient-to-position) !important;
          --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important;
        }
        .mp-brand .to-emerald-500 { --tw-gradient-to: #009137 var(--tw-gradient-to-position) !important; }
        .mp-brand .hover\\:from-emerald-700:hover { --tw-gradient-from: #002a08 var(--tw-gradient-from-position) !important; }
        .mp-brand .hover\\:to-emerald-600:hover   { --tw-gradient-to: #007a2e var(--tw-gradient-to-position) !important; }

        /* ─── Small bright fills (dots, animated bars, accent pills) ─── */
        .mp-brand .bg-emerald-500 { background-color: #009137 !important; }
        .mp-brand .bg-emerald-500\\/10 { background-color: rgba(0,145,55,0.10) !important; }

        /* ─── Surface tints (mint wash for cards/chips) ──────────────── */
        .mp-brand .bg-emerald-50  { background-color: rgba(0,145,55,0.08) !important; }
        .mp-brand .bg-emerald-50\\/60 { background-color: rgba(0,145,55,0.06) !important; }
        .mp-brand .bg-emerald-100 { background-color: rgba(0,145,55,0.14) !important; }

        /* ─── Borders ────────────────────────────────────────────────── */
        .mp-brand .border-emerald-100 { border-color: rgba(1,60,16,0.18) !important; }
        .mp-brand .border-emerald-200 { border-color: rgba(1,60,16,0.28) !important; }
        .mp-brand .border-emerald-200\\/40 { border-color: rgba(1,60,16,0.22) !important; }
        .mp-brand .border-emerald-300 { border-color: rgba(0,145,55,0.45) !important; }
        .mp-brand .border-emerald-400 { border-color: rgba(0,145,55,0.65) !important; }
        .mp-brand .border-emerald-500 { border-color: #009137 !important; }
        .mp-brand .border-emerald-500\\/10 { border-color: rgba(0,145,55,0.18) !important; }
        .mp-brand .hover\\:border-emerald-500:hover { border-color: #009137 !important; }
        .mp-brand .hover\\:border-emerald-300:hover { border-color: rgba(0,145,55,0.55) !important; }

        /* ─── Shadows ────────────────────────────────────────────────── */
        .mp-brand .shadow-emerald-600\\/20 { --tw-shadow-color: rgba(1,60,16,0.22) !important; --tw-shadow: var(--tw-shadow-colored) !important; }
        .mp-brand .shadow-emerald-600\\/30 { --tw-shadow-color: rgba(1,60,16,0.32) !important; --tw-shadow: var(--tw-shadow-colored) !important; }

        /* ─── "Standard" tier label, badges, plus chips → accent ─────── */
        .mp-brand .mp-tier-standard {
          background: rgba(0,145,55,0.10) !important;
          color: #009137 !important;
          border-color: rgba(0,145,55,0.25) !important;
        }
        .mp-brand .mp-badge-std {
          background: rgba(0,145,55,0.08) !important;
          color: #009137 !important;
          border-color: rgba(0,145,55,0.22) !important;
        }
        .mp-brand .mp-card-num,
        .mp-brand .mp-bento-card.expanded .mp-card-num { color: rgba(1,60,16,0.42) !important; }
        .mp-brand .mp-stage-deliver { background: rgba(0,145,55,0.12) !important; color: #009137 !important; }

        /* ─── Feature bento card icons → accent ──────────────────────── */
        .mp-brand .mp-card-icon-box svg { color: #009137 !important; }

        /* ─── Footer: brand-ink → black ──────────────────────────────── */
        .mp-brand footer.bg-slate-950 {
          background: linear-gradient(180deg, #041408 0%, #02090A 50%, #000000 100%) !important;
        }

        /* ─── Animated hero CTA section (9-stop mirror, 14s sweep) ───── */
        .mp-brand .mp-hero-bg {
          background: linear-gradient(135deg, #021708, #013C10, #006425, #009137, #2bd06a, #009137, #006425, #013C10, #021708) !important;
          background-size: 600% 600% !important;
          animation: mp-gradient-shift 14s ease infinite !important;
        }

        /* ─── Old-emerald & teal blobs/dots inside V2 → brand accent ─── */
        .mp-brand .bg-teal-400\\/10 { background-color: rgba(0,145,55,0.10) !important; }
        .mp-brand .bg-teal-400\\/5  { background-color: rgba(0,145,55,0.05) !important; }
        .mp-brand .bg-emerald-400 { background-color: #009137 !important; }
        .mp-brand .bg-emerald-300\\/50 { background-color: rgba(0,145,55,0.45) !important; }
        .mp-brand .bg-emerald-300 { background-color: rgba(0,145,55,0.55) !important; }
        .mp-brand .bg-emerald-200 { background-color: rgba(0,145,55,0.30) !important; }
        /* Dashboard mockup gradient pills ("from-emerald-50 to-teal-50") */
        .mp-brand .from-emerald-50 { --tw-gradient-from: rgba(0,145,55,0.10) var(--tw-gradient-from-position) !important; --tw-gradient-to: rgba(0,145,55,0) var(--tw-gradient-to-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
        .mp-brand .to-teal-50      { --tw-gradient-to: rgba(0,145,55,0.06) var(--tw-gradient-to-position) !important; }
        .mp-brand .from-emerald-50\\/50 { --tw-gradient-from: rgba(0,145,55,0.08) var(--tw-gradient-from-position) !important; --tw-gradient-to: rgba(0,145,55,0) var(--tw-gradient-to-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
        .mp-brand .to-teal-50\\/30  { --tw-gradient-to: rgba(0,145,55,0.05) var(--tw-gradient-to-position) !important; }
        /* Success popup top header: "from-emerald-500 to-teal-600" → brand */
        .mp-brand .from-emerald-500 { --tw-gradient-from: #013C10 var(--tw-gradient-from-position) !important; --tw-gradient-to: rgba(1,60,16,0) var(--tw-gradient-to-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
        .mp-brand .to-teal-600       { --tw-gradient-to: #009137 var(--tw-gradient-to-position) !important; }
        /* Coming-Soon dialog icon background "bg-emerald-100" already remapped via overlay */

        /* ─── Leaflet map pins → new brand accent (subtle size) ────── */
        .mp-brand .mp-lf-pin { width: 16px !important; height: 16px !important; }
        .mp-brand .mp-lf-pin .lf-core {
          width: 7px !important; height: 7px !important;
          top: 4.5px !important; left: 4.5px !important;
          background: radial-gradient(circle, #2bd06a 0%, #009137 60%, #006425 100%) !important;
          box-shadow: 0 0 5px rgba(0,145,55,0.55), 0 0 10px rgba(0,145,55,0.25) !important;
        }
        .mp-brand .mp-lf-pin .lf-pulse  { border-color: rgba(0,145,55,0.42) !important; }
        .mp-brand .mp-lf-pin .lf-pulse2 { border-color: rgba(0,145,55,0.18) !important; }

        /* ─── Accent text (small bright details) ──────────────────────── */
        .mp-brand .text-emerald-400 { color: #009137 !important; }
        .mp-brand .group:hover .group-hover\\:text-emerald-500 { color: #009137 !important; }

        /* ─── Outline-CTA hover ("Need Suggestions?") → white fill, darker border ───── */
        .mp-brand [data-testid="demo-btn"] { transition: border-color .35s ease, background-color .35s ease; }
        .mp-brand [data-testid="demo-btn"]:hover {
          background-color: #ffffff !important;
          border-color: #94a3b8 !important; /* slate-400 — darker than slate-200 default */
        }
      `}</style>

      {/* ══════════ NAV ══════════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-2 h-14 sm:h-16">
            <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-shrink" data-testid="logo">
              <img src="/motopotti-logo.png" alt="" aria-hidden="true" className="h-7 sm:h-9 w-auto flex-shrink-0" />
              <img src="/motopotti-wordmark.png" alt="MotoPotti" className="h-6 sm:h-8 w-auto flex-shrink min-w-0" />
            </a>
            <div className="hidden md:flex items-center gap-8">
              {[
                { id: 'features', en: 'Features', bn: 'ফিচার' },
                { id: 'network', en: 'Network', bn: 'নেটওয়ার্ক' },
                { id: 'pricing', en: 'Pricing', bn: 'মূল্য' },
              ].map(s => (
                <a key={s.id} href={`#${s.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' }); }} className={`text-sm font-semibold transition-colors ${activeSection === s.id ? 'text-emerald-600' : 'text-slate-500 hover:text-emerald-600'}`}>
                  {language === 'en' ? s.en : s.bn}
                </a>
              ))}
            </div>
            <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
              <Button variant="ghost" size="sm" onClick={toggleLanguage} data-testid="language-toggle" className="text-slate-500 px-1.5 sm:px-3 h-9 sm:h-10">
                <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-0.5 sm:mr-1.5" /><span className="text-[11px] sm:text-sm">{language === 'en' ? 'বাংলা' : 'EN'}</span>
              </Button>
              <Link to="/login" data-testid="login-btn"><Button variant="outline" size="sm" className="border-slate-200 text-[11px] sm:text-sm px-2 sm:px-4 h-9 sm:h-10">{language === 'en' ? 'Login' : 'লগইন'}</Button></Link>
              <Button size="sm" onClick={() => setShowSubscribeForm(true)} className="bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/20 text-[11px] sm:text-sm px-2.5 sm:px-4 h-9 sm:h-10 whitespace-nowrap" data-testid="subscribe-btn">
                {language === 'en' ? 'Start Free Trial' : 'ফ্রি ট্রায়াল'}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* ══════════ HERO ══════════ */}
      <header className="relative pt-20 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-8 overflow-hidden bg-slate-50">
        <div className="pointer-events-none absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-emerald-700/10 blur-3xl" />
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-8 sm:gap-16">
          {/* Left — text */}
          <div className="lg:w-1/2 space-y-5 sm:space-y-8 z-10">
            <div className="inline-flex items-center px-3 sm:px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/40 text-emerald-700 font-bold text-[10px] sm:text-xs">
              <Shield className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1.5" />{language === 'en' ? 'Enterprise Grade Management' : 'এন্টারপ্রাইজ গ্রেড ম্যানেজমেন্ট'}
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-[900] font-['Epilogue'] leading-[1.1] tracking-tight text-slate-900">
              {language === 'en' ? (<>Vehicle Service Management<br /><span className="text-emerald-600">Made Simple</span></>) : (<>যানবাহন সার্ভিস ম্যানেজমেন্ট<br /><span className="text-emerald-600">সহজ করুন</span></>)}
            </h1>
            <p className="text-sm sm:text-lg text-slate-500 max-w-xl leading-relaxed">
              {language === 'en'
                ? 'Stop losing money on manual tracking—Empower your garage with a powerful growth engine that combines AI inventory, targeted marketing, and deep financial reports into one seamless platform to skyrocket your profit.'
                : 'ম্যানুয়াল ট্র্যাকিংয়ে টাকা হারানো বন্ধ করুন—AI ইনভেন্টরি, টার্গেটেড মার্কেটিং এবং গভীর আর্থিক রিপোর্ট একত্রিত করে আপনার গ্যারেজের মুনাফা বাড়ান।'}
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <MagneticButton>
                <Button size="lg" className="h-11 sm:h-14 px-5 sm:px-8 text-sm sm:text-lg font-bold bg-gradient-to-r from-emerald-600 to-emerald-500 text-white hover:from-emerald-700 hover:to-emerald-600 shadow-xl shadow-emerald-600/20 rounded-xl" onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} data-testid="hero-cta">
                  {language === 'en' ? 'Get Started for Free' : 'বিনামূল্যে শুরু করুন'}<ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </MagneticButton>
              <a href="tel:+8801786264886">
                <MagneticButton>
                  <Button size="lg" variant="outline" className="h-11 sm:h-14 px-5 sm:px-8 text-sm sm:text-lg font-bold text-slate-900 border-slate-200 hover:bg-slate-50 rounded-xl" data-testid="demo-btn">
                    <Phone className="mr-2 w-5 h-5" />{language === 'en' ? 'Need Suggestions?' : 'পরামর্শ দরকার?'}
                  </Button>
                </MagneticButton>
              </a>
            </div>
          </div>
          {/* Right — glass panel dashboard mockup */}
          <div className="lg:w-1/2 relative">
            <div className="bg-white/70 backdrop-blur-xl border border-emerald-500/10 p-6 rounded-2xl shadow-2xl relative z-20 transition-transform duration-700 hover:-translate-y-4">
              <div className="flex items-center justify-between mb-6">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-emerald-400" />
                </div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{language === 'en' ? 'Job Management Dashboard' : 'জব ম্যানেজমেন্ট ড্যাশবোর্ড'}</div>
              </div>
              <div className="space-y-4">
                <div className="h-12 bg-slate-100 rounded-lg flex items-center px-4 justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center"><Wrench className="w-4 h-4 text-emerald-600" /></div>
                    <div className="h-2 w-32 bg-slate-200 rounded" />
                  </div>
                  <div className="h-2 w-16 bg-emerald-300/50 rounded" />
                </div>
                <div className="h-32 bg-slate-50 rounded-lg p-4 flex gap-4">
                  <div className="w-1/3 bg-white rounded-md shadow-sm border border-slate-100" />
                  <div className="w-2/3 space-y-3 pt-1">
                    <div className="h-2 w-full bg-slate-200/60 rounded" />
                    <div className="h-2 w-3/4 bg-slate-200/60 rounded" />
                    <div className="h-2 w-1/2 bg-slate-200/60 rounded" />
                    <div className="flex gap-2 mt-3">
                      <div className="h-6 w-16 bg-emerald-100 rounded-md" />
                      <div className="h-6 w-16 bg-amber-100 rounded-md" />
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1 h-16 bg-emerald-50 rounded-lg p-3">
                    <div className="h-1.5 w-12 bg-emerald-200 rounded mb-2" />
                    <div className="h-4 w-16 bg-slate-200 rounded" />
                  </div>
                  <div className="flex-1 h-16 bg-slate-50 rounded-lg p-3">
                    <div className="h-1.5 w-12 bg-slate-200 rounded mb-2" />
                    <div className="h-4 w-16 bg-slate-200 rounded" />
                  </div>
                  <div className="flex-1 h-16 bg-slate-50 rounded-lg p-3">
                    <div className="h-1.5 w-12 bg-slate-200 rounded mb-2" />
                    <div className="h-4 w-16 bg-emerald-200 rounded" />
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -z-10" />
            <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-teal-400/10 rounded-full blur-3xl -z-10" />
            {/* Stats strip below dashboard */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
              <div className="bg-white/80 backdrop-blur border border-slate-200/60 rounded-xl px-4 py-3 text-center">
                <div className="text-xl sm:text-2xl font-[900] font-['Epilogue'] text-emerald-600"><CountUp end={Number(landingStats?.hero?.active_garages) || garages.length || 3} />+</div>
                <div className="text-[9px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">{language === 'en' ? 'Active Garages' : 'সক্রিয় গ্যারেজ'}</div>
              </div>
              <div className="bg-white/80 backdrop-blur border border-slate-200/60 rounded-xl px-4 py-3 text-center">
                <div className="text-xl sm:text-2xl font-[900] font-['Epilogue'] text-slate-800">৳<CountUp end={Number(landingStats?.hero?.starting_price) || stdPricing.monthly.discounted} /></div>
                <div className="text-[9px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">{language === 'en' ? 'Starting / Month' : 'শুরু / মাস'}</div>
              </div>
              <div className="bg-white/80 backdrop-blur border border-slate-200/60 rounded-xl px-4 py-3 text-center">
                <div className="text-xl sm:text-2xl font-[900] font-['Epilogue'] text-emerald-600"><CountUp end={landingStats?.hero?.support || '24/7'} /></div>
                <div className="text-[9px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">{language === 'en' ? 'Support' : 'সাপোর্ট'}</div>
              </div>
              <div className="bg-white/80 backdrop-blur border border-slate-200/60 rounded-xl px-4 py-3 text-center">
                <div className="text-xl sm:text-2xl font-[900] font-['Epilogue'] text-slate-800">৳<CountUp end={landingStats?.hero?.revenue_handled || '43L'} suffix="+" /></div>
                <div className="text-[9px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">{language === 'en' ? 'Revenue Handled' : 'রেভিনিউ পরিচালিত'}</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ══════════ PRICING (mobile-first, shown before features on small screens) ══════════ */}
      {isMobile && <PricingSection language={language} billingCycle={billingCycle} setBillingCycle={setBillingCycle} stdPricing={stdPricing} premPricing={premPricing} standardFeatures={standardFeatures} premiumFeatures={premiumFeatures} setSubscribeForm={setSubscribeForm} setShowSubscribeForm={setShowSubscribeForm} />}

      {/* ══════════ FEATURES BENTO GRID ══════════ */}
      <section id="features" ref={featureSectionRef} className="py-20 sm:py-28 px-4 sm:px-8 bg-slate-50/80 relative" style={{ overflow: 'visible' }}>
        {/* Subtle cream blobs — left-center & right-center */}
        <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-[-220px] w-[620px] h-[440px] rounded-full blur-3xl" style={{ background: 'radial-gradient(ellipse at center, rgba(255,243,219,0.48) 0%, rgba(255,243,219,0.13) 55%, rgba(255,243,219,0) 80%)' }} />
        <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -translate-y-1/2 right-[-220px] w-[620px] h-[440px] rounded-full blur-3xl" style={{ background: 'radial-gradient(ellipse at center, rgba(255,243,219,0.48) 0%, rgba(255,243,219,0.13) 55%, rgba(255,243,219,0) 80%)' }} />
        {/* Mobile feature scroll progress */}
        {isMobile && showFeatureProgress && (
          <div className="mp-feature-progress">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">
                {language === 'en' ? 'Features' : 'ফিচার'}
              </span>
              <span className="text-[10px] font-bold text-slate-400">
                {Math.round(featureProgress)}%
              </span>
            </div>
            <div className="mp-feature-progress-bar">
              <div className="mp-feature-progress-fill" style={{ width: `${featureProgress}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[9px] text-slate-400">{language === 'en' ? `${featureCards.length} features` : `${featureCards.length}টি ফিচার`}</span>
              <span className="text-[9px] text-emerald-500 font-medium">{language === 'en' ? 'Next: Our Network ↓' : 'পরবর্তী: নেটওয়ার্ক ↓'}</span>
            </div>
          </div>
        )}
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 sm:mb-12 text-center mp-reveal">
            <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-bold tracking-wider uppercase px-4 py-1.5">
              {language === 'en' ? 'Features' : 'ফিচার'}
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-[900] font-['Epilogue'] text-slate-900 tracking-tight mb-4">
              {language === 'en' ? 'Everything Your Garage Needs' : 'আপনার গ্যারেজের জন্য সবকিছু'}
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-lg">
              {language === 'en' ? 'From the first job card to your last salary payment — MotoPotti runs your entire operation.' : 'প্রথম জব কার্ড থেকে শেষ বেতন পেমেন্ট পর্যন্ত — মোটোপটি আপনার সম্পূর্ণ অপারেশন চালায়।'}
            </p>
          </div>

          {featureCards.length > 0 && (
            <div
              ref={gridRef}
              className={`mp-bento-grid ${activeCard !== null ? 'has-expanded' : ''}`}
              onMouseLeave={() => { if (isMobile) return; clearTimeout(hoverTimer.current); hoverTimer.current = setTimeout(collapseCards, 80); }}
            >
              {featureCards.map((card, idx) => {
                const Icon = resolveIcon(card.icon);
                const isExpanded = activeCard === idx;
                const isPremium = card.plan_tier === 'premium';
                const isWide = card.grid_size === 'W';
                const fonts = CARD_FONT[card.id] || { title: 'text-[12px]', tag: 'text-[9.5px]', exp: 'text-[10.5px]' };
                const shortTitle = CARD_SHORT_TITLES[card.id]?.[language] || (language === 'bn' && card.title_bn ? card.title_bn : card.title);
                const tagline = CARD_TAGLINES[card.id]?.[language] || '';
                const expandedSub = CARD_EXPANDED_SUB[card.id]?.[language] || (language === 'bn' && card.subtitle_bn ? card.subtitle_bn : card.subtitle);
                return (
                  <div
                    key={card.id}
                    className={`mp-bento-card bc-${card.grid_position} ${isExpanded ? 'expanded' : ''}`}
                    onMouseEnter={(e) => { if (isMobile) return; const cardEl = e.currentTarget; clearTimeout(hoverTimer.current); hoverTimer.current = setTimeout(() => expandCard(idx, cardEl), 55); }}
                    onMouseLeave={() => { if (isMobile) return; clearTimeout(hoverTimer.current); }}
                  >
                    <div className={`mp-bento-inner${isPremium && isMobile ? ' mp-mob-premium' : ''}`}>
                      <span className="mp-card-num">{String(idx + 1).padStart(2, '0')}</span>

                      {/* Tier pill badge */}
                      <span className={`mp-tier-pill ${isPremium ? 'mp-tier-premium' : 'mp-tier-standard'}`}>
                        {isPremium ? 'PREMIUM' : 'STANDARD'}
                      </span>

                      {/* Icon */}
                      <div className={`mp-card-icon-box ${isPremium ? 'mp-icon-premium' : ''}`}>
                        <Icon className="w-5 h-5" />
                      </div>

                      {isWide ? (
                        /* Wide cards (7, 10): title + tagline always beside icon */
                        <div className="mp-wide-layout" style={isMobile ? {} : { marginTop: '-32px', marginLeft: '46px' }}>
                          <h3 className={`font-[800] font-['Epilogue'] ${fonts.title} text-slate-900 leading-tight`}>
                            {shortTitle}
                          </h3>
                          <p className={`mp-card-tagline ${fonts.tag} text-slate-500 mt-0.5`}>
                            {tagline}
                          </p>
                        </div>
                      ) : (
                        /* Non-wide: title below icon, slides beside icon on expand */
                        <>
                          <h3 className={`mp-card-title font-[800] font-['Epilogue'] ${fonts.title} text-slate-900 leading-snug`}>
                            {shortTitle}
                          </h3>
                          {tagline && (
                            <p className={`mp-card-tagline ${fonts.tag} text-slate-500`}>
                              {tagline}
                            </p>
                          )}
                        </>
                      )}

                      {/* Always-visible P&L bars for tall income/expense card */}
                      {card.id === 'lc-09' && (
                        <div className="mt-2 space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-[8px] font-bold text-slate-500">Revenue</span>
                            <span className="text-[9px] font-[900] text-slate-900">৳ 84,200</span>
                          </div>
                          <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-600 rounded-full" style={{ width: '100%' }} />
                          </div>
                          <div className="flex justify-between items-center mt-0.5">
                            <span className="text-[8px] font-bold text-slate-500">Expenses</span>
                            <span className="text-[9px] font-[900] text-red-500">৳ 31,500</span>
                          </div>
                          <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-red-400/60 rounded-full" style={{ width: '37%' }} />
                          </div>
                        </div>
                      )}

                      {/* Expanded content */}
                      <div className="mp-exp-content">
                        <p className={`text-slate-500 leading-relaxed ${fonts.exp} mb-1`}>
                          {expandedSub}
                        </p>
                        {renderExpandedContent(card)}
                        {card.asset_url && (
                          <img src={card.asset_url} alt="" className="w-full rounded-lg mt-2 border border-slate-100" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ══════════ DHAKA MAP + TENANT NETWORK ══════════ */}
      <section id="network" className="py-20 sm:py-28 px-4 sm:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 mp-reveal">
            <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-bold tracking-wider uppercase px-4 py-1.5">
              {language === 'en' ? 'Our Network' : 'আমাদের নেটওয়ার্ক'}
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-[900] font-['Epilogue'] text-slate-900 tracking-tight mb-4">
              {language === 'en' ? 'Powering Garages Across Dhaka' : 'ঢাকা জুড়ে গ্যারেজ চালাচ্ছে'}
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-lg">
              {language === 'en'
                ? `${garages.length || 'Many'} workshops trust MotoPotti to run their daily operations.`
                : `${garages.length || 'অনেক'} ওয়ার্কশপ মোটোপটিকে বিশ্বাস করে।`}
            </p>
          </div>

          {/* Dhaka Map — scroll-jacking container */}
          <div ref={mapContainerRef} className="mp-reveal relative max-w-5xl mx-auto w-full h-[360px] sm:h-[560px] lg:h-[620px] rounded-2xl sm:rounded-3xl overflow-hidden mb-8 sm:mb-16">
            <div ref={mapRef} className="absolute inset-0" />

            {/* Left fade gradient — overlays the map */}
            <div className="absolute inset-y-0 left-0 w-[30%] sm:w-[26%] bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none" style={{ zIndex: 500 }} />
            {/* Left stats — on top of fade, with CountUp */}
            <div className="absolute inset-y-0 left-0 w-[30%] sm:w-[26%] hidden md:flex flex-col justify-center items-start pl-4 lg:pl-6 gap-7" style={{ zIndex: 600 }}>
              <div>
                <div className="text-2xl lg:text-3xl font-[900] text-emerald-600 font-['Epilogue']"><CountUp end={Number(landingStats?.network?.active_garages) || garages.length || 8} duration={1.4} />+</div>
                <div className="text-[9px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">{language === 'en' ? 'Active Garages' : 'সক্রিয় গ্যারেজ'}</div>
              </div>
              <div>
                <div className="text-2xl lg:text-3xl font-[900] text-slate-800 font-['Epilogue']"><CountUp end={Number(landingStats?.network?.zones_covered) || 6} duration={1.2} />+</div>
                <div className="text-[9px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">{language === 'en' ? 'Zones Covered' : 'জোন কভার'}</div>
              </div>
              <div>
                <div className="text-2xl lg:text-3xl font-[900] text-emerald-600 font-['Epilogue']"><CountUp end={landingStats?.network?.cloud_support || '24/7'} /></div>
                <div className="text-[9px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">{language === 'en' ? 'Cloud Support' : 'ক্লাউড সাপোর্ট'}</div>
              </div>
            </div>

            {/* Right fade gradient — overlays the map */}
            <div className="absolute inset-y-0 right-0 w-[30%] sm:w-[26%] bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none" style={{ zIndex: 500 }} />
            {/* Right stats — on top of fade, with CountUp */}
            <div className="absolute inset-y-0 right-0 w-[30%] sm:w-[26%] hidden md:flex flex-col justify-center items-end pr-4 lg:pr-6 gap-7" style={{ zIndex: 600 }}>
              <div className="text-right">
                <div className="text-2xl lg:text-3xl font-[900] text-slate-800 font-['Epilogue']"><CountUp end={landingStats?.network?.uptime || '99.9'} suffix="%" /></div>
                <div className="text-[9px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">{language === 'en' ? 'Uptime' : 'আপটাইম'}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl lg:text-3xl font-[900] text-emerald-600 font-['Epilogue']"><CountUp end={landingStats?.network?.jobs_processed || '15K'} suffix="+" /></div>
                <div className="text-[9px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">{language === 'en' ? 'Jobs Processed' : 'কাজ সম্পন্ন'}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl lg:text-3xl font-[900] text-slate-800 font-['Epilogue']"><CountUp end={landingStats?.network?.avg_rating || '4.8'} suffix="★" /></div>
                <div className="text-[9px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">{language === 'en' ? 'Avg Rating' : 'গড় রেটিং'}</div>
              </div>
            </div>

            {/* Location badge – top center with animated text (clickable to advance) */}
            <div
              className="mp-map-badge absolute top-4 left-1/2 -translate-x-1/2 bg-white/85 backdrop-blur-xl border border-emerald-100 rounded-xl px-5 py-2.5 shadow-lg flex items-center gap-3 cursor-pointer select-none"
              style={{ zIndex: 1000 }}
              onClick={() => { if (!mapTransitioning.current) { setActiveMapIndex(prev => (prev + 1) % MAP_VIEWS.length); resetMapAutoScroll(); } }}
            >
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <div className="relative overflow-hidden" style={{ minWidth: 80 }}>
                {MAP_VIEWS.map((view, idx) => (
                  <div
                    key={view.id}
                    className="mp-badge-text"
                    style={{
                      position: idx === 0 ? 'relative' : 'absolute',
                      top: idx === 0 ? undefined : 0,
                      left: idx === 0 ? undefined : 0,
                      opacity: idx === activeMapIndex ? 1 : 0,
                      transform: idx === activeMapIndex ? 'translateY(0)' : idx < activeMapIndex ? 'translateY(-8px)' : 'translateY(8px)',
                    }}
                  >
                    <div className="text-sm font-[900] text-emerald-700 font-['Epilogue'] leading-none">
                      {language === 'bn' ? view.label.bn : view.label.en}
                    </div>
                    <div className="text-[8px] uppercase font-bold tracking-wider text-slate-400">
                      {language === 'bn' ? view.subtitle.bn : view.subtitle.en}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dot indicators – bottom center */}
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2" style={{ zIndex: 1000 }}>
              {MAP_VIEWS.map((view, idx) => (
                <button
                  key={view.id}
                  className={`mp-map-dot ${idx === activeMapIndex ? 'active' : ''}`}
                  onClick={() => { if (!mapTransitioning.current) { setActiveMapIndex(idx); resetMapAutoScroll(); } }}
                  aria-label={view.label.en}
                />
              ))}
            </div>

            {/* Scroll hint removed — map auto-rotates and supports click navigation */}

            {/* Auto-play timer indicator — click to pause/resume */}
            <div className={`mp-map-autoplay${mapAutoPaused ? ' paused' : ''}`} onClick={toggleMapAutoScroll} title={mapAutoPaused ? 'Resume auto-play' : 'Pause auto-play'}>
              <div className="mp-map-timer-ring">
                <svg className="mp-ring-svg" viewBox="0 0 32 32" key={mapAutoPaused ? 'paused' : activeMapIndex}>
                  <circle className="mp-timer-track" cx="16" cy="16" r="13" />
                  <circle className="mp-timer-fill" cx="16" cy="16" r="13" />
                </svg>
                {mapAutoPaused
                  ? <svg className="relative z-10" width="10" height="12" viewBox="0 0 10 12" fill="none"><path d="M1 1l8 5-8 5V1z" fill="#64748b"/></svg>
                  : <svg className="relative z-10" width="8" height="10" viewBox="0 0 8 10" fill="none"><rect x="0" y="0" width="2.5" height="10" rx="0.5" fill="#64748b"/><rect x="5.5" y="0" width="2.5" height="10" rx="0.5" fill="#64748b"/></svg>
                }
              </div>
            </div>

            {/* Mobile stats bar */}
            <div className="absolute bottom-3 left-3 right-3 md:hidden bg-white/80 backdrop-blur-xl border border-emerald-100 rounded-2xl px-4 py-3 flex items-center justify-around shadow-lg" style={{ zIndex: 1000 }}>
              <div className="text-center">
                <div className="text-lg font-[900] text-emerald-600 font-['Epilogue']"><CountUp end={Number(landingStats?.network?.active_garages) || garages.length || 8} duration={1.4} />+</div>
                <div className="text-[8px] uppercase font-bold tracking-wider text-slate-400">{language === 'en' ? 'Garages' : 'গ্যারেজ'}</div>
              </div>
              <div className="w-px h-8 bg-emerald-100" />
              <div className="text-center">
                <div className="text-lg font-[900] text-slate-800 font-['Epilogue']"><CountUp end={Number(landingStats?.network?.zones_covered) || 6} duration={1.2} />+</div>
                <div className="text-[8px] uppercase font-bold tracking-wider text-slate-400">{language === 'en' ? 'Zones' : 'জোন'}</div>
              </div>
              <div className="w-px h-8 bg-emerald-100" />
              <div className="text-center">
                <div className="text-lg font-[900] text-emerald-600 font-['Epilogue']"><CountUp end={landingStats?.network?.jobs_processed || '15K'} suffix="+" /></div>
                <div className="text-[8px] uppercase font-bold tracking-wider text-slate-400">{language === 'en' ? 'Jobs' : 'কাজ'}</div>
              </div>
            </div>
          </div>

          {/* Tenant Cards */}
          {garages.length > 0 && (
            <div className="mp-reveal">
              {garages.length > 3 ? (
                <div className="mp-garage-marquee group/marquee" aria-label="Active garages">
                  <div className="mp-garage-track">
                    {[...garages, ...garages].map((garage, idx) => (
                      <Link key={`${garage.id}-${idx}`} to={`/${garage.slug}`} data-testid={`garage-${garage.slug}`} className="mp-garage-card-wrap">
                        <div className="group bg-white border border-slate-200/80 rounded-xl sm:rounded-2xl p-3 sm:p-5 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-100/40 transition-all duration-300 cursor-pointer h-full">
                          <div className="flex items-center gap-2 sm:gap-4 mb-2 sm:mb-3">
                            {garage.logo_url ? (
                              <img src={garage.logo_url} alt={garage.name} className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl object-cover border border-slate-100" />
                            ) : (
                              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg sm:rounded-xl flex items-center justify-center border border-emerald-100">
                                <Building2 className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-600" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-slate-900 truncate text-xs sm:text-base">{language === 'bn' && garage.name_bn ? garage.name_bn : garage.name}</h3>
                              {garage.phone && <p className="text-[10px] sm:text-xs text-slate-400">{garage.phone}</p>}
                            </div>
                            <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 text-slate-300 group-hover:text-emerald-500 transition-colors flex-shrink-0" />
                          </div>
                          {garage.address && (
                            <p className="text-[10px] sm:text-xs text-slate-500 flex items-start gap-1 sm:gap-1.5">
                              <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 mt-0.5 flex-shrink-0 text-emerald-500" />
                              <span className="line-clamp-1 sm:line-clamp-2">{language === 'bn' && garage.address_bn ? garage.address_bn : garage.address}</span>
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
                  {garages.map((garage) => (
                    <Link key={garage.id} to={`/${garage.slug}`} data-testid={`garage-${garage.slug}`}>
                      <div className="group bg-white border border-slate-200/80 rounded-xl sm:rounded-2xl p-3 sm:p-5 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-100/40 transition-all duration-300 cursor-pointer h-full">
                        <div className="flex items-center gap-2 sm:gap-4 mb-2 sm:mb-3">
                          {garage.logo_url ? (
                            <img src={garage.logo_url} alt={garage.name} className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl object-cover border border-slate-100" />
                          ) : (
                            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg sm:rounded-xl flex items-center justify-center border border-emerald-100">
                              <Building2 className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-600" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-slate-900 truncate text-xs sm:text-base">{language === 'bn' && garage.name_bn ? garage.name_bn : garage.name}</h3>
                            {garage.phone && <p className="text-[10px] sm:text-xs text-slate-400">{garage.phone}</p>}
                          </div>
                          <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 text-slate-300 group-hover:text-emerald-500 transition-colors flex-shrink-0" />
                        </div>
                        {garage.address && (
                          <p className="text-[10px] sm:text-xs text-slate-500 flex items-start gap-1 sm:gap-1.5">
                            <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 mt-0.5 flex-shrink-0 text-emerald-500" />
                            <span className="line-clamp-1 sm:line-clamp-2">{language === 'bn' && garage.address_bn ? garage.address_bn : garage.address}</span>
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ══════════ PRICING ══════════ */}
      {!isMobile && (
      <section id="pricing" className="py-20 sm:py-28 px-4 sm:px-8 bg-slate-50 relative overflow-hidden">
        {/* Cream blob — top-center */}
        <div aria-hidden="true" className="pointer-events-none absolute top-[-260px] left-1/2 -translate-x-1/2 w-[820px] h-[520px] rounded-full blur-3xl" style={{ background: 'radial-gradient(ellipse at center, rgba(255,243,219,0.38) 0%, rgba(255,243,219,0.11) 55%, rgba(255,243,219,0) 80%)' }} />
        {/* Cream blob — bottom-center */}
        <div aria-hidden="true" className="pointer-events-none absolute bottom-[-280px] left-1/2 -translate-x-1/2 w-[820px] h-[560px] rounded-full blur-3xl" style={{ background: 'radial-gradient(ellipse at center, rgba(255,243,219,0.38) 0%, rgba(255,243,219,0.11) 55%, rgba(255,243,219,0) 80%)' }} />
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6 mp-reveal">
            <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-bold tracking-wider uppercase px-4 py-1.5">
              {language === 'en' ? 'Subscription Pricing' : 'সাবস্ক্রিপশন মূল্য'}
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-[900] font-['Epilogue'] text-slate-900 tracking-tight mb-4">
              {language === 'en' ? 'Simple Pricing. Serious Value.' : 'সহজ মূল্য। গুরুতর মূল্য।'}
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-lg">
              {language === 'en'
                ? 'Pick the plan that fits your garage. No hidden fees. Pay longer, save more.'
                : 'আপনার গ্যারেজের জন্য সঠিক প্ল্যান বেছে নিন। কোনো লুকানো ফি নেই।'}
            </p>
          </div>

          {/* Billing toggle */}
          <div className="flex justify-center mb-12 mp-reveal">
            <div className="relative bg-slate-100 rounded-full p-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
              {/* Animated pill background */}
              <div
                className="absolute top-1 bottom-1 bg-white rounded-full shadow-md transition-all duration-300 ease-out"
                style={{
                  left: billingCycle === 'monthly' ? '4px' : billingCycle === 'half-yearly' ? 'calc(33.33% + 1px)' : 'calc(66.66% - 1px)',
                  width: 'calc(33.33% - 3px)',
                }}
              />
              {[
                { key: 'monthly', label: language === 'en' ? 'Monthly' : 'মাসিক' },
                { key: 'half-yearly', label: language === 'en' ? 'Half-Yearly' : 'অর্ধ-বার্ষিক', badge: language === 'en' ? '1 month FREE' : '১ মাস ফ্রি' },
                { key: 'yearly', label: language === 'en' ? 'Yearly' : 'বার্ষিক', badge: language === 'en' ? '3 months FREE' : '৩ মাস ফ্রি' },
              ].map(b => (
                <button
                  key={b.key}
                  onClick={() => setBillingCycle(b.key)}
                  className={`relative z-10 px-4 sm:px-6 py-2.5 rounded-full text-sm font-bold transition-colors duration-300 whitespace-nowrap text-center ${billingCycle === b.key ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {b.label}
                  {b.badge && billingCycle === b.key && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap animate-[fadeSlideIn_0.3s_ease-out]">{b.badge}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mp-reveal">
            {/* Pricing Card Generator */}
            {[
              { key: 'standard', label: 'Standard', pricing: stdPricing, features: standardFeatures, isPremium: false },
              { key: 'premium', label: 'Premium', pricing: premPricing, features: premiumFeatures, isPremium: true },
            ].map(({ key: planKey, label, pricing, features, isPremium }) => {
              const cycleData = pricing[billingCycle === 'half-yearly' ? 'half-yearly' : billingCycle];
              const totalPrice = cycleData?.discounted || 0;
              const months = billingCycle === 'yearly' ? 12 : billingCycle === 'half-yearly' ? 6 : 1;
              const perMonth = Math.round(totalPrice / months);
              const regularTotal = billingCycle === 'monthly' ? pricing.monthly.regular * 1 : billingCycle === 'half-yearly' ? pricing.monthly.regular * 6 : pricing.monthly.regular * 12;
              const savings = regularTotal - totalPrice;
              const cycleLabel = billingCycle === 'half-yearly' ? (language === 'en' ? '6 months' : '৬ মাস') : billingCycle === 'yearly' ? (language === 'en' ? '1 year' : '১ বছর') : (language === 'en' ? '1 month' : '১ মাস');

              return (
                <div key={planKey} className={`mp-pricing-card bg-white rounded-3xl ${isPremium ? 'border-2 border-emerald-500 shadow-2xl shadow-emerald-100/50' : 'border border-slate-200'} p-8 lg:p-10 flex flex-col relative`}>
                  {isPremium && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-5 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 shadow-lg shadow-emerald-600/30">
                      <Star className="w-3 h-3 fill-current" />{language === 'en' ? 'Most Popular' : 'সবচেয়ে জনপ্রিয়'}
                    </div>
                  )}
                  <h3 className="text-2xl font-[800] font-['Epilogue'] text-slate-900 mb-2">{label}</h3>
                  <p className="text-sm text-slate-500 mb-6">{language === 'en' ? pricing.tagline : pricing.tagline_bn}</p>

                  {/* Per-month price — always shown as the hero number */}
                  <div className="mb-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-emerald-600 text-lg font-bold">৳</span>
                      <span className="text-5xl font-[900] font-['Epilogue'] text-slate-900">
                        <AnimatedPrice value={perMonth} />
                      </span>
                      <span className="text-slate-400 text-sm font-medium">/{language === 'en' ? 'mo' : 'মাস'}</span>
                    </div>
                  </div>

                  {/* Total and savings — bigger text */}
                  <div className="mb-6 transition-all duration-500 ease-out overflow-hidden" style={{ maxHeight: billingCycle !== 'monthly' || pricing.monthly.regular > pricing.monthly.discounted ? '80px' : '0px', opacity: billingCycle !== 'monthly' || pricing.monthly.regular > pricing.monthly.discounted ? 1 : 0 }}>
                    {billingCycle !== 'monthly' && (
                      <div key={billingCycle} className="animate-[fadeSlideIn_0.3s_ease-out]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-lg font-[800] text-slate-700">৳{totalPrice.toLocaleString()}</span>
                          <span className="text-sm text-slate-400 font-medium">{language === 'en' ? 'for' : 'জন্য'} {cycleLabel}</span>
                        </div>
                        {savings > 0 && (
                          <div className="mt-1.5">
                            <span className="bg-emerald-100 text-emerald-700 text-sm font-bold px-3 py-1 rounded-full inline-flex items-center gap-1">
                              <Sparkles className="w-3.5 h-3.5" />
                              {language === 'en' ? `Save ৳${savings.toLocaleString()}` : `সাশ্রয় ৳${savings.toLocaleString()}`}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {billingCycle === 'monthly' && pricing.monthly.regular > pricing.monthly.discounted && (
                      <div className="animate-[fadeSlideIn_0.3s_ease-out]">
                        <span className="text-slate-400 text-sm line-through">৳{pricing.monthly.regular.toLocaleString()}/{language === 'en' ? 'mo' : 'মাস'}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-2">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-4">
                      {isPremium
                        ? (language === 'en' ? (<>Everything in <span className="text-emerald-600">Standard</span>, <span className="text-emerald-600 font-[900]">plus</span>:</>) : (<><span className="text-emerald-600">Standard</span>-এর সবকিছু, <span className="text-emerald-600 font-[900]">এবং</span>:</>))
                        : (language === 'en' ? "What's included" : 'যা অন্তর্ভুক্ত')
                      }
                    </p>
                    <ul className="space-y-3 mb-6">
                      {features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm"><CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span className="text-slate-700">{language === 'bn' ? f.bn : f.en}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-auto pt-6">
                    <Button
                      className={`w-full h-12 text-base font-bold rounded-xl ${isPremium ? 'bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/20' : 'border-2 border-slate-200 hover:border-emerald-500 hover:text-emerald-600'}`}
                      variant={isPremium ? 'default' : 'outline'}
                      onClick={() => { setSubscribeForm(f => ({ ...f, plan: planKey, billing_cycle: billingCycle })); setShowSubscribeForm(true); }}
                      data-testid={`select-${planKey}`}
                    >
                      {language === 'en' ? 'Get Started' : 'শুরু করুন'}{isPremium && <ArrowRight className="ml-2 w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* ══════════ CTA ══════════ */}
      <section className="py-12 sm:py-20 md:py-28 px-4 sm:px-8 mp-hero-bg relative overflow-hidden">
        <div className="mp-cta-ambient" aria-hidden="true" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none"><div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-400/5 blur-3xl" /></div>
        <div className="max-w-4xl mx-auto text-center relative z-10 mp-reveal">
          <h2 className="text-xl sm:text-4xl md:text-5xl font-[900] font-['Epilogue'] text-white mb-3 sm:mb-6 tracking-tight">
            {language === 'en' ? 'Ready to Digitize Your Garage?' : 'আপনার গ্যারেজ ডিজিটাল করতে প্রস্তুত?'}
          </h2>
          <p className="text-sm sm:text-lg text-emerald-100/70 mb-6 sm:mb-10 max-w-2xl mx-auto">
            {language === 'en' ? 'Join the garages already using MotoPotti. Start your free trial today — no credit card required.' : 'মোটোপটি ব্যবহারকারী গ্যারেজগুলিতে যোগ দিন। আজই ফ্রি ট্রায়াল শুরু করুন।'}
          </p>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            <MagneticButton>
              <Button size="lg" className="h-11 sm:h-14 px-5 sm:px-8 text-sm sm:text-lg font-bold bg-white text-emerald-700 hover:bg-emerald-50 shadow-2xl rounded-xl" onClick={() => setShowSubscribeForm(true)}>
                {language === 'en' ? 'Get Started' : 'শুরু করুন'}<ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </MagneticButton>
            <a href="tel:+8801786264886">
              <MagneticButton>
                <Button size="lg" variant="outline" className="h-11 sm:h-14 px-5 sm:px-8 text-sm sm:text-lg font-bold text-white border-white/20 hover:bg-white/10 rounded-xl">
                  <Phone className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />{language === 'en' ? 'Need Suggestions?' : 'পরামর্শ দরকার?'}
                </Button>
              </MagneticButton>
            </a>
          </div>
        </div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="bg-slate-950 text-white py-8 sm:py-16 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Mobile: compact stacked layout | Desktop: 4-col grid */}
          <div className="hidden md:grid md:grid-cols-4 gap-10">
            {/* Desktop footer - unchanged */}
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/motopotti-logo.png" alt="" aria-hidden="true" className="h-10 w-auto" />
                <img src="/motopotti-wordmark.png" alt="MotoPotti" className="h-9 w-auto" />
              </div>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">{language === 'en' ? 'The complete SaaS platform for vehicle service centers in Bangladesh.' : 'বাংলাদেশের বাইক ও কার সার্ভিস সেন্টারের জন্য সম্পূর্ণ SaaS প্ল্যাটফর্ম।'}</p>
              <div className="flex items-center gap-3">
                <a href="https://www.facebook.com/profile.php?id=61575740207468" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-blue-600 flex items-center justify-center transition-colors" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
                <a href="https://www.instagram.com/motopotti/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-pink-600 flex items-center justify-center transition-colors" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
                <button onClick={() => setShowComingSoon('YouTube')} className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-red-600 flex items-center justify-center transition-colors" aria-label="YouTube"><Youtube className="w-4 h-4" /></button>
                <button onClick={() => setShowComingSoon('TikTok')} className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors" aria-label="TikTok">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.51a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.81a8.23 8.23 0 004.76 1.52V6.88a4.85 4.85 0 01-1-.19z"/></svg>
                </button>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-slate-300">{language === 'en' ? 'Contact' : 'যোগাযোগ'}</h4>
              <div className="space-y-3 text-slate-400 text-sm">
                <p className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-emerald-500" /> 01786264886</p>
                <p className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-emerald-500" /> contact@motopotti.com</p>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-slate-300">{language === 'en' ? 'Quick Links' : 'দ্রুত লিংক'}</h4>
              <div className="space-y-2.5 text-slate-400 text-sm">
                <a href="#features" className="block hover:text-white transition-colors">{language === 'en' ? 'Features' : 'ফিচার'}</a>
                <a href="#pricing" className="block hover:text-white transition-colors">{language === 'en' ? 'Pricing' : 'মূল্য'}</a>
                <Link to="/hardware" className="block hover:text-white transition-colors">{language === 'en' ? 'Hardware' : 'হার্ডওয়্যার'}</Link>
                <Link to="/login" className="block hover:text-white transition-colors">{language === 'en' ? 'Login' : 'লগইন'}</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-slate-300">{language === 'en' ? 'Get the App' : 'অ্যাপ ডাউনলোড'}</h4>
              <div className="space-y-2.5">
                <button onClick={() => setShowComingSoon('App Store')} className="w-full flex items-center gap-3 bg-slate-800 hover:bg-slate-700 rounded-xl px-4 py-2.5 transition-colors text-left">
                  <Apple className="w-5 h-5 text-slate-300" />
                  <div><div className="text-[9px] text-slate-400 uppercase">Download on the</div><div className="text-sm font-bold text-white -mt-0.5">App Store</div></div>
                </button>
                <button onClick={() => setShowComingSoon('Play Store')} className="w-full flex items-center gap-3 bg-slate-800 hover:bg-slate-700 rounded-xl px-4 py-2.5 transition-colors text-left">
                  <PlayCircle className="w-5 h-5 text-slate-300" />
                  <div><div className="text-[9px] text-slate-400 uppercase">Get it on</div><div className="text-sm font-bold text-white -mt-0.5">Google Play</div></div>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile footer - compact */}
          <div className="md:hidden">
            {/* Brand + social row */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <img src="/motopotti-logo.png" alt="" aria-hidden="true" className="h-8 w-auto" />
                <img src="/motopotti-wordmark.png" alt="MotoPotti" className="h-7 w-auto" />
              </div>
              <div className="flex items-center gap-2">
                <a href="https://www.facebook.com/profile.php?id=61575740207468" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-md bg-slate-800 flex items-center justify-center" aria-label="Facebook"><Facebook className="w-3.5 h-3.5" /></a>
                <a href="https://www.instagram.com/motopotti/" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-md bg-slate-800 flex items-center justify-center" aria-label="Instagram"><Instagram className="w-3.5 h-3.5" /></a>
                <button onClick={() => setShowComingSoon('YouTube')} className="w-7 h-7 rounded-md bg-slate-800 flex items-center justify-center" aria-label="YouTube"><Youtube className="w-3.5 h-3.5" /></button>
                <button onClick={() => setShowComingSoon('TikTok')} className="w-7 h-7 rounded-md bg-slate-800 flex items-center justify-center" aria-label="TikTok">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.51a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.81a8.23 8.23 0 004.76 1.52V6.88a4.85 4.85 0 01-1-.19z"/></svg>
                </button>
              </div>
            </div>
            <p className="text-slate-500 text-xs leading-relaxed mb-4">{language === 'en' ? 'The complete SaaS platform for vehicle service centers in Bangladesh.' : 'বাংলাদেশের বাইক ও কার সার্ভিস সেন্টারের জন্য সম্পূর্ণ SaaS প্ল্যাটফর্ম।'}</p>

            {/* Contact + Quick Links - 2 columns */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-0 mb-4">
              <div>
                <h4 className="font-bold mb-1.5 text-[10px] uppercase tracking-wider text-slate-400">{language === 'en' ? 'Contact' : 'যোগাযোগ'}</h4>
                <div className="space-y-1 text-slate-400 text-[11px]">
                  <p className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-emerald-500" /> 01786264886</p>
                  <p className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-emerald-500" /> contact@motopotti.com</p>
                </div>
              </div>
              <div>
                <h4 className="font-bold mb-1.5 text-[10px] uppercase tracking-wider text-slate-400">{language === 'en' ? 'Quick Links' : 'দ্রুত লিংক'}</h4>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-slate-400 text-[11px]">
                  <a href="#features" className="hover:text-white transition-colors">{language === 'en' ? 'Features' : 'ফিচার'}</a>
                  <a href="#pricing" className="hover:text-white transition-colors">{language === 'en' ? 'Pricing' : 'মূল্য'}</a>
                  <Link to="/hardware" className="hover:text-white transition-colors">{language === 'en' ? 'Hardware' : 'হার্ডওয়্যার'}</Link>
                  <Link to="/login" className="hover:text-white transition-colors">{language === 'en' ? 'Login' : 'লগইন'}</Link>
                </div>
              </div>
            </div>

            {/* App buttons - side by side */}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setShowComingSoon('App Store')} className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-2 text-left">
                <Apple className="w-4 h-4 text-slate-300" />
                <div><div className="text-[7px] text-slate-400 uppercase">Download on the</div><div className="text-xs font-bold text-white -mt-0.5">App Store</div></div>
              </button>
              <button onClick={() => setShowComingSoon('Play Store')} className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-2 text-left">
                <PlayCircle className="w-4 h-4 text-slate-300" />
                <div><div className="text-[7px] text-slate-400 uppercase">Get it on</div><div className="text-xs font-bold text-white -mt-0.5">Google Play</div></div>
              </button>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 sm:mt-10 pt-6 sm:pt-8 text-center text-slate-500 text-xs sm:text-sm">
            <p>© 2026 MotoPotti. {language === 'en' ? 'All rights reserved.' : 'সর্বস্বত্ব সংরক্ষিত।'}</p>
          </div>
        </div>
      </footer>

      {/* ══════════ COMING SOON DIALOG ══════════ */}
      <Dialog open={!!showComingSoon} onOpenChange={() => setShowComingSoon('')}>
        <DialogContent className="mp-brand max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="font-['Epilogue'] font-[800] text-xl">{language === 'en' ? 'Coming Soon!' : 'শীঘ্রই আসছে!'}</DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-slate-600">{language === 'en' ? `Our ${showComingSoon} is coming soon. Stay tuned!` : `আমাদের ${showComingSoon} শীঘ্রই আসছে। সাথে থাকুন!`}</p>
            <p className="text-sm text-slate-400 mt-2">{language === 'en' ? 'Follow us on social media for updates.' : 'আপডেটের জন্য সোশ্যাল মিডিয়ায় ফলো করুন।'}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══════════ DEMO DIALOG ══════════ */}
      <Dialog open={showDemoForm} onOpenChange={setShowDemoForm}>
        <DialogContent className="mp-brand max-w-md">
          <DialogHeader><DialogTitle className="font-['Epilogue'] font-[800]">{language === 'en' ? 'Book a Demo' : 'ডেমো বুক করুন'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{language === 'en' ? 'Garage Name' : 'গ্যারেজের নাম'}</Label><Input value={demoForm.garage_name} onChange={(e) => setDemoForm(p => ({ ...p, garage_name: e.target.value }))} data-testid="demo-garage-name" /></div>
            <div><Label>{language === 'en' ? 'Your Name' : 'আপনার নাম'}</Label><Input value={demoForm.contact_name} onChange={(e) => setDemoForm(p => ({ ...p, contact_name: e.target.value }))} data-testid="demo-contact-name" /></div>
            <div><Label>{language === 'en' ? 'Email' : 'ইমেইল'}</Label><Input type="email" value={demoForm.email} onChange={(e) => setDemoForm(p => ({ ...p, email: e.target.value }))} data-testid="demo-email" /></div>
            <div><Label>{language === 'en' ? 'Phone' : 'ফোন'}</Label><Input value={demoForm.phone} onChange={(e) => setDemoForm(p => ({ ...p, phone: e.target.value }))} data-testid="demo-phone" /></div>
            <div><Label>{language === 'en' ? 'Message (Optional)' : 'বার্তা (ঐচ্ছিক)'}</Label><Textarea value={demoForm.message} onChange={(e) => setDemoForm(p => ({ ...p, message: e.target.value }))} /></div>
            <MagneticButton className="block w-full">
              <Button onClick={submitDemoRequest} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-demo">{language === 'en' ? 'Submit Request' : 'অনুরোধ জমা দিন'}</Button>
            </MagneticButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══════════ SUBSCRIBE DIALOG (Redesigned Multi-Step) ══════════ */}
      <Dialog open={showSubscribeForm} onOpenChange={(open) => { setShowSubscribeForm(open); if (!open) { setSubFormErrors({}); setShowSubPassword(false); setSubStep(1); removeVoucher(); setSubscribeForm(f => ({ ...f, free_trial: false })); } }}>
        <DialogContent className={`mp-brand ${subStep === 2 ? 'max-w-3xl' : 'max-w-lg'} max-h-[92vh] overflow-y-auto p-0 transition-all duration-300`} aria-describedby={undefined}>
          {/* Header with step indicator */}
          <div className="p-6 pb-0">
            <DialogHeader><DialogTitle className="font-['Epilogue'] font-[800] text-xl">{language === 'en' ? 'Subscribe to MotoPotti' : 'মোটোপটিতে সাবস্ক্রাইব করুন'}</DialogTitle></DialogHeader>
            <div className="flex items-center gap-2 mt-4">
              <div className={`flex items-center gap-2 flex-1 ${subStep >= 1 ? 'text-emerald-600' : 'text-slate-300'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${subStep >= 1 ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-400'}`}>1</div>
                <span className="text-xs font-bold hidden sm:block">{language === 'en' ? 'Garage Info' : 'গ্যারেজ তথ্য'}</span>
              </div>
              <div className={`h-0.5 flex-1 rounded transition-all duration-500 ${subStep >= 2 ? 'bg-emerald-500' : 'bg-slate-200'}`} />
              <div className={`flex items-center gap-2 flex-1 justify-end ${subStep >= 2 ? 'text-emerald-600' : 'text-slate-300'}`}>
                <span className="text-xs font-bold hidden sm:block">{language === 'en' ? 'Plan & Payment' : 'প্ল্যান ও পেমেন্ট'}</span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${subStep >= 2 ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-400'}`}>2</div>
              </div>
            </div>
          </div>

          <div className="p-6 pt-4">
            {/* Step 1: Garage Info */}
            {subStep === 1 && (
              <div className="space-y-4 animate-[fadeSlideIn_0.5s_ease-in-out]">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">{language === 'en' ? 'Garage Name (EN)' : 'গ্যারেজের নাম (EN)'} <span className="text-red-500">*</span></Label>
                    <Input value={subscribeForm.garage_name} onChange={(e) => { setSubscribeForm(p => ({ ...p, garage_name: e.target.value })); setSubFormErrors(p => ({ ...p, garage_name: false })); }} className={`mt-1 ${subFormErrors.garage_name ? 'border-red-500' : ''}`} placeholder="e.g. ABC Auto Service" data-testid="sub-garage-name" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">{language === 'en' ? 'Garage Name (BN)' : 'গ্যারেজের নাম (বাংলা)'}</Label>
                    <Input value={subscribeForm.garage_name_bn} onChange={(e) => setSubscribeForm(p => ({ ...p, garage_name_bn: e.target.value }))} className="mt-1" placeholder="ঐচ্ছিক" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500">{language === 'en' ? 'Owner Name' : 'মালিকের নাম'} <span className="text-red-500">*</span></Label>
                  <Input value={subscribeForm.owner_name} onChange={(e) => { setSubscribeForm(p => ({ ...p, owner_name: e.target.value })); setSubFormErrors(p => ({ ...p, owner_name: false })); }} className={`mt-1 ${subFormErrors.owner_name ? 'border-red-500' : ''}`} data-testid="sub-owner-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">{language === 'en' ? 'Email' : 'ইমেইল'} <span className="text-red-500">*</span></Label>
                    <Input type="email" value={subscribeForm.email} onChange={(e) => { setSubscribeForm(p => ({ ...p, email: e.target.value })); setSubFormErrors(p => ({ ...p, email: false })); }} className={`mt-1 ${subFormErrors.email ? 'border-red-500' : ''}`} data-testid="sub-email" />
                    {subFormErrors.email && subscribeForm.email && <p className="text-[10px] text-red-500 mt-0.5">{language === 'en' ? 'Enter a valid email address' : 'সঠিক ইমেইল দিন'}</p>}
                    <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1"><Mail className="w-3 h-3" />{language === 'en' ? 'This email will be used to log in to your dashboard.' : 'এই ইমেইল দিয়ে ড্যাশবোর্ডে লগইন করতে হবে।'}</p>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">{language === 'en' ? 'Phone' : 'ফোন'} <span className="text-red-500">*</span></Label>
                    <Input type="tel" maxLength={11} value={subscribeForm.phone} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 11); setSubscribeForm(p => ({ ...p, phone: v })); setSubFormErrors(p => ({ ...p, phone: false })); }} placeholder="01XXXXXXXXX" className={`mt-1 ${subFormErrors.phone ? 'border-red-500' : ''}`} data-testid="sub-phone" />
                    {subFormErrors.phone && subscribeForm.phone && <p className="text-[10px] text-red-500 mt-0.5">{language === 'en' ? 'Must be 11 digits starting with 01' : '০১ দিয়ে শুরু ১১ সংখ্যা হতে হবে'}</p>}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500">{language === 'en' ? 'Password' : 'পাসওয়ার্ড'} <span className="text-red-500">*</span></Label>
                  <div className="relative mt-1">
                    <Input type={showSubPassword ? 'text' : 'password'} value={subscribeForm.password} onChange={(e) => { setSubscribeForm(p => ({ ...p, password: e.target.value })); setSubFormErrors(p => ({ ...p, password: false })); }} placeholder={language === 'en' ? 'Min 6 characters' : 'ন্যূনতম ৬ অক্ষর'} className={`pr-10 ${subFormErrors.password ? 'border-red-500' : ''}`} data-testid="sub-password" />
                    <button type="button" onClick={() => setShowSubPassword(!showSubPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">{showSubPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1"><Shield className="w-3 h-3" />{language === 'en' ? 'You\'ll need this password for first login. You can change it later from settings.' : 'প্রথম লগইনে এই পাসওয়ার্ড লাগবে। পরে সেটিংস থেকে পরিবর্তন করতে পারবেন।'}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500">{language === 'en' ? 'Address' : 'ঠিকানা'} <span className="text-red-500">*</span></Label>
                  <Input value={subscribeForm.address} onChange={(e) => { setSubscribeForm(p => ({ ...p, address: e.target.value })); setSubFormErrors(p => ({ ...p, address: false })); }} className={`mt-1 ${subFormErrors.address ? 'border-red-500' : ''}`} data-testid="sub-address" />
                </div>
                <MagneticButton className="w-full">
                  <Button className="w-full h-11 font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700" onClick={() => { if (validateSubForm()) setSubStep(2); }}>
                    {language === 'en' ? 'Next: Choose Plan' : 'পরবর্তী: প্ল্যান বেছে নিন'}<ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </MagneticButton>
              </div>
            )}

            {/* Step 2: Plan & Payment — 2 column layout */}
            {subStep === 2 && (() => {
              const activePricing = subscribeForm.plan === 'premium' ? premPricing : stdPricing;
              const cycle = subscribeForm.billing_cycle;
              const baseAmount = subscribeForm.free_trial ? 0 : (activePricing[cycle === 'half-yearly' ? 'half-yearly' : cycle]?.discounted || 0);
              const discountAmt = (!subscribeForm.free_trial && voucherApplied) ? Math.round((activePricing[cycle === 'half-yearly' ? 'half-yearly' : cycle]?.discounted || 0) * voucherApplied.discount_percent / 100) : 0;
              const finalAmount = subscribeForm.free_trial ? 0 : (activePricing[cycle === 'half-yearly' ? 'half-yearly' : cycle]?.discounted || 0) - discountAmt;
              const months = cycle === 'yearly' ? 12 : cycle === 'half-yearly' ? 6 : 1;
              const cycleLabel = cycle === 'half-yearly' ? (language === 'en' ? '6 months' : '৬ মাস') : cycle === 'yearly' ? (language === 'en' ? '1 year' : '১ বছর') : (language === 'en' ? '1 month' : '১ মাস');

              return (
                <div className="grid md:grid-cols-2 gap-6 animate-[fadeSlideIn_0.5s_ease-in-out]">
                  {/* LEFT Column: Plan + Billing + Voucher */}
                  <div className="space-y-4">
                    {/* Plan selector cards */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 mb-2 block">{language === 'en' ? 'Select Plan' : 'প্ল্যান নির্বাচন করুন'}</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {['standard', 'premium'].map(planKey => {
                          const p = planKey === 'premium' ? premPricing : stdPricing;
                          const isSelected = subscribeForm.plan === planKey;
                          return (
                            <button key={planKey} type="button" onClick={() => { setSubscribeForm(f => ({ ...f, plan: planKey })); if (voucherApplied && voucherApplied.plan_restriction && voucherApplied.plan_restriction !== planKey) removeVoucher(); }}
                              className={`relative text-left p-4 rounded-xl border-2 transition-all duration-200 ${isSelected ? 'border-emerald-500 bg-emerald-50/50 shadow-md scale-[1.02]' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            >
                              {planKey === 'premium' && <span className="absolute -top-2 right-2 bg-emerald-600 text-white text-[8px] font-bold px-2 py-0.5 rounded-full uppercase">{language === 'en' ? 'Popular' : 'জনপ্রিয়'}</span>}
                              <div className="text-sm font-[800] text-slate-900 capitalize">{planKey}</div>
                              <div className="text-lg font-[900] text-emerald-600 mt-1">৳{p.monthly.discounted.toLocaleString()}<span className="text-xs font-normal text-slate-400">/{language === 'en' ? 'mo' : 'মাস'}</span></div>
                              {isSelected && <div className="absolute top-2 left-2"><CheckCircle className="w-4 h-4 text-emerald-600" /></div>}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Billing cycle */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-500">{language === 'en' ? 'Billing Cycle' : 'বিলিং সাইকেল'}</Label>
                      <div className="grid grid-cols-3 gap-2 mt-1.5">
                        {[
                          { key: 'monthly', en: 'Monthly', bn: 'মাসিক' },
                          { key: 'half-yearly', en: 'Half-Yearly', bn: 'অর্ধ-বার্ষিক', badge: language === 'en' ? '1 mo free' : '১ মাস ফ্রি' },
                          { key: 'yearly', en: 'Yearly', bn: 'বার্ষিক', badge: language === 'en' ? '3 mo free' : '৩ মাস ফ্রি' },
                        ].map(c => {
                          const disabled = subscribeForm.free_trial && c.key !== 'monthly';
                          return (
                          <button key={c.key} type="button" onClick={() => !disabled && setSubscribeForm(f => ({ ...f, billing_cycle: c.key }))}
                            className={`relative p-2.5 rounded-xl border-2 text-center text-xs font-bold transition-all duration-200 ${disabled ? 'opacity-40 cursor-not-allowed border-slate-200 text-slate-400' : subscribeForm.billing_cycle === c.key ? 'border-emerald-500 bg-emerald-50 text-emerald-700 scale-[1.02]' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                          >
                            {language === 'en' ? c.en : c.bn}
                            {c.badge && <span className="block text-[9px] font-bold text-emerald-600 mt-0.5">{c.badge}</span>}
                          </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Voucher section — dotted coupon style (hidden when free trial is on) */}
                    {!subscribeForm.free_trial && (
                    <div className="relative">
                      <div className="border-2 border-dashed border-emerald-300 rounded-2xl p-4 bg-gradient-to-br from-emerald-50/50 to-teal-50/30 relative overflow-hidden">
                        {/* Decorative circles on edges */}
                        <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full border-2 border-dashed border-emerald-300" />
                        <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full border-2 border-dashed border-emerald-300" />

                        <div className="flex items-center gap-2 mb-3">
                          <Tag className="w-4 h-4 text-emerald-600" />
                          <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">{language === 'en' ? 'Have a Voucher?' : 'ভাউচার আছে?'}</span>
                        </div>

                        {!voucherApplied ? (
                          <div>
                            <div className="flex gap-2">
                              <Input
                                value={voucherCode}
                                onChange={(e) => { setVoucherCode(e.target.value.toUpperCase()); setVoucherError(''); }}
                                placeholder={language === 'en' ? 'Enter voucher code' : 'ভাউচার কোড লিখুন'}
                                className="flex-1 bg-white border-emerald-200 text-sm font-mono uppercase tracking-wider"
                                onKeyDown={(e) => { if (e.key === 'Enter') applyVoucher(); }}
                              />
                              <Button
                                type="button"
                                onClick={applyVoucher}
                                disabled={voucherLoading || !voucherCode.trim()}
                                className="bg-emerald-600 hover:bg-emerald-700 text-xs font-bold px-4"
                                size="sm"
                              >
                                {voucherLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (language === 'en' ? 'Apply' : 'প্রয়োগ')}
                              </Button>
                            </div>
                            {voucherError && <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1"><Info className="w-3 h-3" />{voucherError}</p>}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-emerald-200 animate-[fadeSlideIn_0.3s_ease-out]">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              </div>
                              <div>
                                <span className="text-sm font-[800] text-emerald-700 font-mono">{voucherApplied.code}</span>
                                <span className="block text-[10px] text-emerald-600">{voucherApplied.discount_percent}% {language === 'en' ? 'discount applied' : 'ছাড় প্রয়োগ হয়েছে'}</span>
                              </div>
                            </div>
                            <button type="button" onClick={removeVoucher} className="text-xs text-red-500 hover:text-red-700 font-bold">✕</button>
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Free trial toggle */}
                    <div className={`p-4 rounded-xl border-2 transition-all duration-300 ${subscribeForm.free_trial ? 'bg-emerald-50/80 border-emerald-400 shadow-md' : 'bg-slate-50 border-slate-200/80'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${subscribeForm.free_trial ? 'bg-emerald-100' : 'bg-slate-100'}`}><Clock className={`w-3.5 h-3.5 ${subscribeForm.free_trial ? 'text-emerald-600' : 'text-slate-400'}`} /></div>
                          <div>
                            <p className={`text-xs font-semibold ${subscribeForm.free_trial ? 'text-emerald-700' : 'text-slate-600'}`}>{language === 'en' ? '30-Day Free Trial' : '৩০ দিনের ফ্রি ট্রায়াল'}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{language === 'en' ? 'Try MotoPotti free for 30 days. Monthly plan only.' : '৩০ দিন বিনামূল্যে ব্যবহার করুন। শুধু মাসিক প্ল্যান।'}</p>
                          </div>
                        </div>
                        <Switch checked={subscribeForm.free_trial} onCheckedChange={(checked) => {
                          setSubscribeForm(f => ({ ...f, free_trial: checked, ...(checked ? { billing_cycle: 'monthly' } : {}) }));
                          if (checked) removeVoucher();
                        }} />
                      </div>
                      {subscribeForm.free_trial && (
                        <div className="mt-3 pt-3 border-t border-emerald-200/60 animate-[fadeSlideIn_0.3s_ease-out]">
                          <div className="flex items-center gap-2 text-xs text-emerald-700">
                            <CheckCircle className="w-3.5 h-3.5" />
                            <span className="font-semibold">{language === 'en' ? 'Trial ends:' : 'ট্রায়াল শেষ:'} {(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); })()}</span>
                          </div>
                          <p className="text-[10px] text-emerald-600/80 mt-1 ml-5.5">{language === 'en' ? 'Full access to all features. No payment required during trial.' : 'সকল ফিচারে সম্পূর্ণ অ্যাক্সেস। ট্রায়ালে পেমেন্ট লাগবে না।'}</p>
                        </div>
                      )}
                    </div>

                    {/* COMMENTED OUT: 15-Day Free Trial + Money-Back Guarantee banners
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 flex items-start gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5"><Clock className="w-3.5 h-3.5 text-slate-400" /></div>
                      <div>
                        <p className="text-xs font-semibold text-slate-600">{language === 'en' ? '15-Day Free Trial Included' : '১৫ দিনের ফ্রি ট্রায়াল অন্তর্ভুক্ত'}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{language === 'en' ? 'Includes 15 extra days free trial on top of your billing period.' : 'বিলিং পিরিয়ডের উপরে ১৫ দিন ফ্রি ট্রায়াল পাবেন।'}</p>
                      </div>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 flex items-start gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5"><Shield className="w-3.5 h-3.5 text-slate-400" /></div>
                      <div>
                        <p className="text-xs font-semibold text-slate-600">{language === 'en' ? 'Money-Back Guarantee' : 'মানি-ব্যাক গ্যারান্টি'}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{language === 'en' ? 'Full refund within 15 days if not satisfied.' : 'সন্তুষ্ট না হলে ১৫ দিনের মধ্যে রিফান্ড।'}</p>
                      </div>
                    </div>
                    */}
                  </div>

                  {/* RIGHT Column: Order summary + Payment */}
                  <div className="space-y-4">
                    {/* Order summary */}
                    <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-50 to-emerald-50/30 border border-slate-200">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{language === 'en' ? 'Order Summary' : 'অর্ডার সারাংশ'}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500 capitalize">{subscribeForm.plan} — {cycleLabel}</span>
                          <span className={`font-bold ${subscribeForm.free_trial ? 'text-slate-400 line-through' : 'text-slate-700'}`}>৳{(activePricing[cycle === 'half-yearly' ? 'half-yearly' : cycle]?.discounted || 0).toLocaleString()}</span>
                        </div>
                        {subscribeForm.free_trial && (
                          <div className="flex justify-between text-sm animate-[fadeSlideIn_0.2s_ease-out]">
                            <span className="text-emerald-600 flex items-center gap-1"><Clock className="w-3 h-3" />{language === 'en' ? '30-Day Free Trial' : '৩০ দিনের ফ্রি ট্রায়াল'}</span>
                            <span className="font-bold text-emerald-600">{language === 'en' ? 'FREE' : 'বিনামূল্যে'}</span>
                          </div>
                        )}
                        {!subscribeForm.free_trial && voucherApplied && (
                          <div className="flex justify-between text-sm animate-[fadeSlideIn_0.2s_ease-out]">
                            <span className="text-emerald-600 flex items-center gap-1"><Tag className="w-3 h-3" /><span className="font-mono font-bold">{voucherApplied.code}</span> ({voucherApplied.discount_percent}%)</span>
                            <span className="font-bold text-emerald-600">-৳{discountAmt.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="border-t border-dashed border-slate-200 pt-2 mt-2">
                          <div className="flex justify-between items-baseline">
                            <span className="text-sm font-bold text-slate-700">{language === 'en' ? 'Total' : 'মোট'}</span>
                            <div className="text-right">
                              <span className={`text-2xl font-[900] font-['Epilogue'] ${subscribeForm.free_trial ? 'text-emerald-600' : 'text-slate-900'}`}>৳{finalAmount.toLocaleString()}</span>
                              {!subscribeForm.free_trial && months > 1 && <span className="block text-[10px] text-slate-400">= ৳{Math.round(finalAmount / months).toLocaleString()}/{language === 'en' ? 'mo' : 'মাস'}</span>}
                            </div>
                          </div>
                        </div>
                        {/* Subscription period / next billing */}
                        <div className="border-t border-dashed border-slate-200 pt-2 mt-2">
                          {subscribeForm.free_trial ? (
                            <>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-400">{language === 'en' ? 'Free trial period' : 'ফ্রি ট্রায়াল পিরিয়ড'}</span>
                                <span className="text-emerald-600 font-semibold">30 {language === 'en' ? 'days' : 'দিন'}</span>
                              </div>
                              <div className="flex justify-between text-xs mt-1">
                                <span className="text-slate-400">{language === 'en' ? 'Trial ends on' : 'ট্রায়াল শেষ'}</span>
                                <span className="text-emerald-600 font-semibold">{(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); })()}</span>
                              </div>
                              <div className="flex justify-between text-xs mt-1">
                                <span className="text-slate-400">{language === 'en' ? 'First payment due' : 'প্রথম পেমেন্ট'}</span>
                                <span className="text-slate-600 font-semibold">{(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); })()}</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-400">{language === 'en' ? 'Subscription period' : 'সাবস্ক্রিপশন সময়কাল'}</span>
                                <span className="text-slate-600 font-semibold">{cycleLabel}</span>
                              </div>
                              <div className="flex justify-between text-xs mt-1">
                                <span className="text-slate-400">{language === 'en' ? 'Next billing date' : 'পরবর্তী বিলিং তারিখ'}</span>
                                <span className="text-slate-600 font-semibold">{(() => { const d = new Date(); d.setDate(d.getDate() + (cycle === 'yearly' ? 365 : cycle === 'half-yearly' ? 180 : 30)); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); })()}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Payment method — hidden during free trial */}
                    {!subscribeForm.free_trial && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-500">{language === 'en' ? 'Payment Method' : 'পেমেন্ট পদ্ধতি'}</Label>
                      <div className="grid grid-cols-3 gap-2 mt-1.5">
                        {[
                          { key: 'bkash', label: 'bKash', color: 'bg-pink-50 border-pink-200 text-pink-700' },
                          { key: 'nagad', label: 'Nagad', color: 'bg-orange-50 border-orange-200 text-orange-700' },
                          { key: 'manual', label: language === 'en' ? 'Manual' : 'ম্যানুয়াল', color: 'bg-slate-50 border-slate-200 text-slate-700' },
                        ].map(m => (
                          <button key={m.key} type="button" onClick={() => setSubscribeForm(f => ({ ...f, payment_method: m.key }))}
                            className={`p-2.5 rounded-xl border-2 text-center text-xs font-bold transition-all duration-200 ${subscribeForm.payment_method === m.key ? `${m.color} shadow-sm scale-[1.02]` : 'border-slate-200 text-slate-400 hover:border-slate-300'}`}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    )}

                    {!subscribeForm.free_trial && subscribeForm.payment_method !== 'manual' && (
                      <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100 space-y-3 animate-[fadeSlideIn_0.2s_ease-out]">
                        <div>
                          <p className="text-xs font-semibold text-emerald-700 mb-1.5">{language === 'en' ? 'Send payment to:' : 'পেমেন্ট পাঠান:'}</p>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 bg-white px-3 py-2 rounded-lg text-base font-[900] text-slate-900 border border-emerald-200 tracking-wider">{PAYMENT_NUMBER}</code>
                            <Button type="button" variant="outline" size="sm" onClick={copyPaymentNumber} className="h-9 px-3 border-emerald-200 text-emerald-700 hover:bg-emerald-100">
                              {copiedNumber ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                          <p className="text-[10px] text-emerald-600 mt-1 capitalize">{language === 'en' ? `via ${subscribeForm.payment_method}` : `${subscribeForm.payment_method} এর মাধ্যমে`}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-slate-500">{language === 'en' ? 'Transaction ID' : 'ট্রানজেকশন আইডি'}</Label>
                            <Input value={subscribeForm.transaction_id} onChange={(e) => setSubscribeForm(p => ({ ...p, transaction_id: e.target.value }))} placeholder="TRX12345..." className="mt-1" data-testid="sub-trx-id" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">{language === 'en' ? 'Sender Phone' : 'প্রেরকের ফোন'}</Label>
                            <Input value={subscribeForm.payment_phone} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 11); setSubscribeForm(p => ({ ...p, payment_phone: v })); }} placeholder="01XXXXXXXXX" className={`mt-1 ${subscribeForm.payment_phone && !/^01\d{9}$/.test(subscribeForm.payment_phone) ? 'border-red-500' : ''}`} />
                            {subscribeForm.payment_phone && !/^01\d{9}$/.test(subscribeForm.payment_phone) && <p className="text-[10px] text-red-500 mt-0.5">{language === 'en' ? '11 digits starting with 01' : '০১ দিয়ে শুরু ১১ সংখ্যা'}</p>}
                          </div>
                        </div>
                      </div>
                    )}
                    {!subscribeForm.free_trial && subscribeForm.payment_method === 'manual' && (
                      <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 flex items-start gap-2.5 animate-[fadeSlideIn_0.2s_ease-out]">
                        <Info className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600">{language === 'en' ? 'Our team will contact you for payment processing.' : 'আমাদের টিম পেমেন্ট প্রসেসিংয়ের জন্য যোগাযোগ করবে।'}</p>
                          <p className="text-xs text-slate-400 mt-1">{language === 'en' ? 'Call/WhatsApp:' : 'কল/হোয়াটসঅ্যাপ:'} {PAYMENT_NUMBER}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <Button variant="outline" className="h-11 rounded-xl px-4" onClick={() => setSubStep(1)}>
                        <ArrowLeft className="w-4 h-4 mr-1" />{language === 'en' ? 'Back' : 'পিছনে'}
                      </Button>
                      <MagneticButton className="flex-1">
                        <Button onClick={submitSubscription} disabled={loading} className="w-full h-11 font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-70" data-testid="submit-subscription">
                        {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{language === 'en' ? 'Submitting...' : 'জমা হচ্ছে...'}</> : subscribeForm.free_trial ? (language === 'en' ? 'Start Free Trial' : 'ফ্রি ট্রায়াল শুরু') : (language === 'en' ? 'Submit Request' : 'অনুরোধ জমা দিন')}
                      </Button>
                      </MagneticButton>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Animations keyframes */}
          <style>{`
            @keyframes fadeSlideIn {
              from { opacity: 0; transform: translateY(4px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
        </DialogContent>
      </Dialog>

      {/* ══════════ SUCCESS POPUP + CONFETTI ══════════ */}
      {confettiPieces.length > 0 && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
          {confettiPieces.map(p => (
            <div key={p.id} style={{
              position: 'absolute', left: `${p.left}%`, top: '-10px',
              width: p.size, height: p.size * (0.6 + Math.random() * 0.8),
              backgroundColor: p.color,
              borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              transform: `rotate(${p.rotation}deg)`,
              animation: `mp-confetti-fall ${p.duration}s ease-out ${p.delay}s forwards`,
              opacity: 0,
            }} />
          ))}
          <style>{`
            @keyframes mp-confetti-fall {
              0% { opacity: 1; transform: translateY(0) rotate(0deg) scale(1); }
              100% { opacity: 0; transform: translateY(100vh) rotate(720deg) scale(0.3); }
            }
          `}</style>
        </div>
      )}

      <Dialog open={showSuccessPopup} onOpenChange={setShowSuccessPopup}>
        <DialogContent className="mp-brand max-w-md text-center p-0 overflow-hidden" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Subscription Submitted</DialogTitle>
          {/* Green header */}
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-8 pb-6">
            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-[900] font-['Epilogue'] text-white">{language === 'en' ? 'Request Submitted!' : 'অনুরোধ জমা হয়েছে!'}</h3>
            <p className="text-emerald-100 text-sm mt-1">{language === 'en' ? 'Your subscription is being processed' : 'আপনার সাবস্ক্রিপশন প্রক্রিয়াধীন'}</p>
          </div>

          <div className="p-6 space-y-4">
            {/* What happens next */}
            <div className="bg-slate-50 rounded-xl p-4 text-left space-y-3">
              <p className="text-sm font-bold text-slate-700">{language === 'en' ? 'What happens next?' : 'এরপর কি হবে?'}</p>
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-bold text-emerald-700">1</span></div>
                <p className="text-xs text-slate-500">{language === 'en' ? 'We will manually verify your payment within 24 hours.' : 'আমরা ২৪ ঘণ্টার মধ্যে আপনার পেমেন্ট ম্যানুয়ালি যাচাই করব।'}</p>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-bold text-emerald-700">2</span></div>
                <p className="text-xs text-slate-500">{language === 'en' ? 'Once verified, your garage dashboard will be activated and we\'ll contact you.' : 'যাচাই হলে আপনার গ্যারেজ ড্যাশবোর্ড সক্রিয় হবে এবং আমরা যোগাযোগ করব।'}</p>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-bold text-emerald-700">3</span></div>
                <p className="text-xs text-slate-500">{language === 'en' ? 'All billing is manual — we\'ll remind you before your next payment is due.' : 'সব বিলিং ম্যানুয়াল — পরবর্তী পেমেন্টের আগে আমরা আপনাকে রিমাইন্ড করব।'}</p>
              </div>
            </div>

            {successData && (
              <div className="space-y-3 text-left">
                {/* Voucher discount banner */}
                {successData.voucherCode && (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-200 animate-[fadeSlideIn_0.3s_ease-out]">
                    <Tag className="w-4 h-4 text-emerald-600" />
                    <div className="flex-1">
                      <span className="text-xs font-bold text-emerald-700">{successData.voucherCode}</span>
                      <span className="text-xs text-emerald-600 ml-1">— {successData.voucherDiscount}% {language === 'en' ? 'off' : 'ছাড়'}</span>
                    </div>
                    <span className="text-sm font-bold text-emerald-700">-৳{successData.discountAmount?.toLocaleString()}</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <div className="text-[10px] text-slate-400 font-bold uppercase">{language === 'en' ? 'Plan' : 'প্ল্যান'}</div>
                    <div className="text-sm font-bold capitalize text-slate-800">{successData.plan}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <div className="text-[10px] text-slate-400 font-bold uppercase">{language === 'en' ? 'Amount' : 'পরিমাণ'}</div>
                    <div className="text-sm font-bold text-slate-800">
                      ৳{successData.amount?.toLocaleString()}
                      {successData.originalAmount && <span className="text-xs text-slate-400 line-through ml-1">৳{successData.originalAmount?.toLocaleString()}</span>}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <div className="text-[10px] text-slate-400 font-bold uppercase">{language === 'en' ? 'Billing' : 'বিলিং'}</div>
                    <div className="text-sm font-bold capitalize text-slate-800">{successData.billing_cycle === 'half-yearly' ? (language === 'en' ? 'Half-Yearly' : 'অর্ধ-বার্ষিক') : successData.billing_cycle === 'yearly' ? (language === 'en' ? 'Yearly' : 'বার্ষিক') : (language === 'en' ? 'Monthly' : 'মাসিক')}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <div className="text-[10px] text-slate-400 font-bold uppercase">{language === 'en' ? 'Next Bill' : 'পরবর্তী বিল'}</div>
                    <div className="text-sm font-bold text-slate-800">{successData.nextBillDate}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Contact info */}
            <div className="flex items-center justify-center gap-4 pt-2 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> 01786264886</span>
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> contact@motopotti.com</span>
            </div>

            <Button className="w-full h-11 font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowSuccessPopup(false)}>
              {language === 'en' ? 'Got it!' : 'বুঝেছি!'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
